
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import '@fontsource/exo-2';
import './StrategyStyles.css';
import './BattleUI.css'; // Import for ability tooltip styles
import OrderDesign from './OrderDesign';
import { useNavigate } from 'react-router-dom';
import CustomDropdown from './CustomDropdown';
import { useTranslation } from 'react-i18next';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import { useMusic } from './hooks/useMusic';
import { MAIN_MENU_MUSIC_PATH } from './config/mainMenuMusic';
import { playSoundEffect } from './hooks/useSoundEffects';
import { resolvePublicAssetUrl, resolvePublicAssetBackgroundUrl } from './utils/resolvePublicAssetUrl';
import { API_URL } from './config/apiConfig';
import { getDeployedUnitInfo, getSpawnSourceParentOrderIds } from './utils/deploySpawnUnitInfo';
import { preloadAllOrderBorderImages } from './utils/borderImageCache';
import {
  DEV_CREATED_CATALOG_FACTION,
  fetchStrategyOrdersCatalog,
  awaitStrategyOrdersCatalogWithMedia,
} from './utils/strategyOrdersCatalogPrefetch';
import { buildOrderDetailLangQuery } from './utils/buildOrderDetailLangQuery';
import { useNotifications } from './notifications';
import { useContainerScale } from './hooks/useViewportScale';
import { useBattleStatTooltip } from './hooks/useBattleStatTooltip';
import { TutorialMessageBox, TutorialReplayButton, useMenuTutorialReplay } from './tutorial/index';
import { isPlayer1DevUser } from './tutorial/isPlayer1DevUser';
import HideDevElementsForScreenshotButton from './devmode/HideDevElementsForScreenshotButton';
import { useHideDevElementsForScreenshot } from './devmode/useHideDevElementsForScreenshot';
import { useTutorialStatus } from './hooks/useTutorialStatus';
import {
  loadAlternateArtDisplayContextFromProgress,
  patchAlternateArtDisplayPref,
  withAlternateArtDisplayOrder,
  isAlternateArtUnlocked,
  getOrderArtVariant,
} from './utils/alternateArtDisplay';
import './components/OrderSidebarStyleCard.css';
import MenuBackButton from './components/MenuBackButton';
import OrderSidebarOrderList from './components/OrderSidebarOrderList';
import AttackStrategyStatisticsPanel from './components/AttackStrategyStatisticsPanel';
import { sidebarMessageGroupKey } from './utils/orderSidebarDisplay';
import {
  registerStrategyDraftGetter,
  unregisterStrategyDraftGetter,
} from './matchmaking/strategyInterruptDraft';
import {
  BATTLE_UI_END_TURN_BUTTON_PATH,
  MENU_PAINTED_SURFACE_FILTER,
} from './config/welcomeAssets';
import './StrategyModal.css';

/**
 * Applied as CSS transform on the portal wrapper (OrderDesign's `scale` prop does not affect
 * the always-expanded 390px branch used when minimal=false and gameState is missing).
 */
const STRATEGY_DEPLOY_SPAWN_PREVIEW_SCALE = 1.62;

const factionColors = {
  'Remnants of Humanity': '#556B2F',
  'Exolot Dominion': '#4B0082',
  'League of Shadows': '#1A1A1A',
  'Ra\'s Children': '#F4A460',
  'Asrah': '#8B0000',
  'The Sentients': '#ADD8E6',
  'Combined': '#FFA500'
};

const factionIconByName = {
  'Remnants of Humanity': 'remnants.png',
  'Exolot Dominion': 'exolot.png',
  'League of Shadows': 'league.png',
  'Ra\'s Children': 'ra.png',
  'Asrah': 'asrah.png',
  'The Conspiracy': 'conspiracy.png',
  'The Corrupted Core': 'core.png',
  'The Covenant': 'covenant.png',
  'The Divine Syndicate': 'syndicate.png',
  'The Forsaken Pact': 'forsaken.png',
  'The Hollow': 'hollow.png',
  'The Iron Alliance': 'alliance.png',
  'The Last Crusade': 'crusade.png',
  'The Shadow Consortium': 'consortium.png',
  'The Silent Accord': 'silent.png',
  'The Sentients': 'sentients.png',
};

const getFactionIconPath = (factionName) => {
  if (!factionName || factionName === 'Unknown') {
    return null;
  }

  const iconFile = factionIconByName[factionName];
  if (!iconFile) {
    throw new Error(`Strategy faction icon mapping missing for faction: ${factionName}`);
  }

  return resolvePublicAssetUrl(`/factions/${iconFile}`);
};

// Get faction orb background color/gradient (matching CommandCenter implementation)
const getFactionOrbBackground = (factionName) => {
  if (!factionName || factionName === 'Unknown') {
    return '#ccc';
  }

  // Check if it's a combined faction and return gradient
  if (factionName === 'The Conspiracy') {
    return 'linear-gradient(to right, #8B0000 50%, #1A1A1A 50%)';
  } else if (factionName === 'The Corrupted Core') {
    return 'linear-gradient(to right, #4B0082 50%, #8B0000 50%)';
  } else if (factionName === 'The Covenant') {
    return 'linear-gradient(to right, #F4A460 50%, #1A1A1A 50%)';
  } else if (factionName === 'The Divine Syndicate') {
    return 'linear-gradient(to right, #F4A460 50%, #4B0082 50%)';
  } else if (factionName === 'The Forsaken Pact') {
    return 'linear-gradient(to right, #556B2F 50%, #8B0000 50%)';
  } else if (factionName === 'The Hollow') {
    return 'linear-gradient(to right, #F4A460 50%, #8B0000 50%)';
  } else if (factionName === 'The Iron Alliance') {
    return 'linear-gradient(to right, #556B2F 50%, #4B0082 50%)';
  } else if (factionName === 'The Last Crusade') {
    return 'linear-gradient(to right, #F4A460 50%, #556B2F 50%)';
  } else if (factionName === 'The Shadow Consortium') {
    return 'linear-gradient(to right, #1A1A1A 50%, #4B0082 50%)';
  } else if (factionName === 'The Silent Accord') {
    return 'linear-gradient(to right, #556B2F 50%, #1A1A1A 50%)';
  }

  // Return base faction color
  return factionColors[factionName] || '#ccc';
};
  
  const combinedFactions = {
    'The Conspiracy': ['The Conspiracy', 'League of Shadows', 'Asrah', 'The Sentients'],
    'The Corrupted Core': ['The Corrupted Core', 'Exolot Dominion', 'Asrah', 'The Sentients'],
    'The Covenant': ['The Covenant', 'Ra\'s Children', 'League of Shadows', 'The Sentients'],
    'The Divine Syndicate': ['The Divine Syndicate', 'Ra\'s Children', 'Exolot Dominion', 'The Sentients'],
    'The Forsaken Pact': ['The Forsaken Pact', 'Remnants of Humanity', 'Asrah', 'The Sentients'],
    'The Hollow': ['The Hollow', 'Ra\'s Children', 'Asrah', 'The Sentients'],
    'The Iron Alliance': ['The Iron Alliance', 'Remnants of Humanity', 'Exolot Dominion', 'The Sentients'],
    'The Last Crusade': ['The Last Crusade', 'Ra\'s Children', 'Remnants of Humanity', 'The Sentients'],
    'The Shadow Consortium': ['The Shadow Consortium', 'League of Shadows', 'Exolot Dominion', 'The Sentients'],
    'The Silent Accord': ['The Silent Accord', 'Remnants of Humanity', 'League of Shadows', 'The Sentients']
    };
    
    const allFactions = [
    'Remnants of Humanity',
    'Exolot Dominion',
    'League of Shadows',
    'Ra\'s Children',
    'Asrah',
    'The Conspiracy',
    'The Corrupted Core',
    'The Covenant',
    'The Divine Syndicate',
    'The Forsaken Pact',
    'The Hollow',
    'The Iron Alliance',
    'The Last Crusade',
    'The Shadow Consortium',
    'The Silent Accord'
    ];

// Helper function to get rarity sort order
const getRarityOrder = (rarity) => {
  const rarityOrder = { 'common': 1, 'rare': 2, 'epic': 3, 'legendary': 4 };
  return rarityOrder[(rarity || 'common').toLowerCase()] || 0;
};

function createSidebarOrderEntry(order, isOwned) {
  if (!order || order.id == null) {
    throw new Error('createSidebarOrderEntry: order.id is required');
  }
  return {
    orderId: order.id,
    name: order.name,
    cost: order.cost,
    isOwned: isOwned !== false,
  };
}

function buildOrderCountsFromSidebarMessages(messages) {
  const counts = {};
  for (const msg of messages || []) {
    if (msg.orderId == null) continue;
    const id = String(msg.orderId);
    counts[id] = (counts[id] || 0) + 1;
  }
  return counts;
}

function normalizeOrderCountsMap(counts) {
  const normalized = {};
  Object.keys(counts || {}).forEach((key) => {
    const id = String(key);
    if (id === 'undefined' || id === 'null') return;
    normalized[id] = (normalized[id] || 0) + counts[key];
  });
  return normalized;
}

function decrementOrderCountMap(counts, orderId) {
  const id = String(orderId);
  const updated = { ...counts };
  const key = Object.keys(updated).find((k) => String(k) === id) ?? id;
  if (updated[key] == null) return updated;
  updated[key] -= 1;
  if (updated[key] <= 0) delete updated[key];
  return updated;
}

function sidebarMessagesMatchForRemoval(msg, target) {
  if (target.orderId != null && msg.orderId != null) {
    return (
      String(msg.orderId) === String(target.orderId) &&
      (msg.isOwned === false) === (target.isOwned === false)
    );
  }
  return (
    msg.name === target.name &&
    msg.cost === target.cost &&
    msg.isOwned === target.isOwned
  );
}

function resolveSidebarOrderRecord(msg, orders, missingOrders) {
  if (msg.orderId != null) {
    const id = Number(msg.orderId);
    const byId =
      (orders || []).find((o) => Number(o.id) === id) ||
      (missingOrders || []).find((o) => Number(o.id) === id);
    if (byId) return byId;
  }
  return (
    (orders || []).find((o) => o.name === msg.name && o.cost === msg.cost) ||
    (missingOrders || []).find((o) => o.name === msg.name && o.cost === msg.cost) ||
    null
  );
}

function buildOriginalCountsFromStrategyOrders(strategyOrders) {
  const originalCounts = {};
  (strategyOrders || []).forEach(({ orderId, order_id, count }) => {
    const id = String(orderId || order_id);
    if (id && id !== 'undefined' && id !== 'null') {
      originalCounts[id] = (originalCounts[id] || 0) + count;
    }
  });
  return originalCounts;
}

/** Unit voice preview applies to deployable units only (matches battle deploy-sound exclusions). */
function strategyOrderShowsUnitSound(order) {
  const type = String(order?.type || '').trim().toLowerCase();
  return type !== 'weapon' && type !== 'drone' && type !== 'strategem' && type !== 'stratagem';
}

/** Voice clip under `public/sounds/id{N}.mp3` — same convention as deploy previews in battle. */
function playStrategyOrderUnitSound(orderId) {
  const id = Number(orderId);
  if (!Number.isFinite(id) || id <= 0) {
    console.warn('playStrategyOrderUnitSound: invalid order id', orderId);
    return;
  }
  playSoundEffect(`/sounds/id${id}.mp3`);
}

function StrategyOrderSpeakerButton({ orderId, ariaLabel }) {
  return (
    <button
      type="button"
      className="strategy-order-speaker-btn"
      aria-label={ariaLabel}
      title={ariaLabel}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        playStrategyOrderUnitSound(orderId);
      }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true" focusable="false">
        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
      </svg>
    </button>
  );
}


/** True when strategy grid `hoveredOrderId` refers to this order (handles string vs number IDs and `missing-*`). */
function strategyOrdersMatchHover(hoveredOrderId, orderId) {
  if (hoveredOrderId == null || orderId == null) return false;
  if (typeof hoveredOrderId === 'string' && hoveredOrderId.startsWith('missing-')) {
    const suffix = hoveredOrderId.slice('missing-'.length);
    return Number(suffix) === Number(orderId);
  }
  return Number(hoveredOrderId) === Number(orderId);
}

// Memoized order card so only the hovered (and previously hovered) card re-render on hover
// Display format: unassigned/total (e.g. 3/3 = 3 copies owned, 0 assigned; 1/3 = 3 owned, 2 assigned)
const StrategyOrderCard = React.memo(function StrategyOrderCard({
  order,
  isHovered,
  onHover,
  onHoverEnd,
  onOrderClickId,
  orderCount,
  displayOwnedCount,
  maxCount,
  canPurchase,
  isDuplicateMode,
  shouldGrayOut,
  ownedCount,
  isMobile,
  unassignedCount,
  totalCount,
  showDebugOrderId,
  playUnitSoundLabel,
  onHoverMove,
  canSwitchAlternateArt,
  artVariant,
  onToggleAlternateArt,
  switchAlternateArtLabel,
}) {
  const displayOrder = withAlternateArtDisplayOrder(order);
  const variant = Number.isInteger(artVariant) && artVariant >= 0 ? artVariant : 0;

  return (
    <div
      onMouseEnter={(e) => onHover(order, e)}
      onMouseMove={(e) => onHoverMove?.(order, e)}
      onMouseLeave={() => onHoverEnd(order)}
      onClick={(e) => {
        if (canPurchase && !isDuplicateMode) {
          e.stopPropagation();
          e.preventDefault();
          onOrderClickId(order.id);
          return;
        }
      }}
      style={{
        cursor: shouldGrayOut && ownedCount >= 1 && (orderCount >= maxCount) ? 'pointer' : (shouldGrayOut ? (canPurchase ? 'pointer' : 'not-allowed') : 'pointer'),
        position: 'relative',
        willChange: !isMobile ? 'transform' : 'auto',
      }}
      className={`strategy-order-card ${shouldGrayOut ? 'order-unowned' : ''} ${isMobile ? 'mobile-order' : ''}`}
      data-mobile-order={isMobile}
    >
      {strategyOrderShowsUnitSound(order) && (
        <StrategyOrderSpeakerButton orderId={order.id} ariaLabel={playUnitSoundLabel} />
      )}
      {canSwitchAlternateArt && (
        <button
          type="button"
          className="strategy-alternate-art-toggle"
          aria-pressed={variant > 0}
          title={switchAlternateArtLabel}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onToggleAlternateArt?.(order.id);
          }}
        >
          {switchAlternateArtLabel}
        </button>
      )}
      <div
        style={{
          filter: shouldGrayOut ? 'grayscale(100%)' : 'none',
          pointerEvents: 'auto',
          position: 'relative',
          zIndex: canPurchase ? 10 : 1,
        }}
        onClick={(e) => {
          if (canPurchase && !isDuplicateMode) {
            e.stopPropagation();
            onOrderClickId(order.id);
          }
        }}
      >
        <OrderDesign
          key={`${order.id}-art-v${variant}`}
          order={displayOrder}
          count={orderCount}
          ownedCount={displayOwnedCount}
          minimal={false}
          lazyLoad={false}
          showOwnedCounter={true}
          maxCount={maxCount}
          rarity={order.rarity}
          onClick={(e) => {
            if (e) e.stopPropagation();
            onOrderClickId(order.id);
          }}
        />
        {showDebugOrderId && (
          <span className="strategy-order-id-debug">
            ID: {order.id}
          </span>
        )}
      </div>
      {canPurchase && isHovered && (
        <div
          className="purchase-dollar-sign"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onOrderClickId(order.id);
          }}
          style={{
            pointerEvents: 'auto !important',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >$</div>
      )}
      {order.rarity && (
        <div
          style={{
            position: 'absolute',
            bottom: '15px',
            right: '15px',
            fontSize: '0.9em',
            fontWeight: 'bold',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            padding: '2px 6px',
            borderRadius: '4px',
            color: (() => {
              const r = (order.rarity || '').toLowerCase();
              if (r === 'common') return '#808080';
              if (r === 'rare') return '#4169E1';
              if (r === 'epic') return '#9370DB';
              if (r === 'legendary') return '#FFD700';
              return '#FFFFFF';
            })(),
            textShadow: (() => {
              const r = (order.rarity || '').toLowerCase();
              if (r === 'common') return '0 0 5px rgba(128, 128, 128, 0.8), 0 0 10px rgba(128, 128, 128, 0.6)';
              if (r === 'rare') return '0 0 5px rgba(65, 105, 225, 0.8), 0 0 10px rgba(65, 105, 225, 0.6)';
              if (r === 'epic') return '0 0 5px rgba(147, 112, 219, 0.8), 0 0 10px rgba(147, 112, 219, 0.6)';
              if (r === 'legendary') return '0 0 5px rgba(255, 215, 0, 0.8), 0 0 10px rgba(255, 215, 0, 0.6), 0 0 15px rgba(255, 215, 0, 0.4)';
              return 'none';
            })(),
            zIndex: 10002,
            pointerEvents: 'none',
          }}
        >
          {unassignedCount}/{totalCount}
        </div>
      )}
    </div>
  );
});

function fuzzyMatch(text, query) {
  if (!query) return true;
  const normalizedText = (text || '').toLowerCase();
  const words = query.toLowerCase().split(/\s+/);
  return words.every(word => normalizedText.includes(word));
}

function Strategy({ user, onLogout }) {
  const { t, i18n: i18nInstance } = useTranslation();
  const playUnitSoundLabel = t('strategy.playUnitSound');
  const switchAlternateArtLabel = t('strategy.switchAlternateArt', { defaultValue: 'Switch art' });
  const showDebugOrderIdsBase = String(user || '').toLowerCase() === 'player1';
  const isPlayer1Dev = isPlayer1DevUser(user);
  const {
    hide: hideDevElementsForScreenshotNow,
    showDevControls: showPlayer1DevUi,
  } = useHideDevElementsForScreenshot();
  const showStrategyPlayer1DevControls = isPlayer1Dev && showPlayer1DevUi;
  const showDebugOrderIds = showDebugOrderIdsBase && showPlayer1DevUi;
  const [devStrategyKeyInput, setDevStrategyKeyInput] = useState('');
  const [devStrategyKeyLoading, setDevStrategyKeyLoading] = useState(false);
  const pendingDevStrategyLoadRef = useRef(null);
  
  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();

  useMusic(MAIN_MENU_MUSIC_PATH);
  
  // Notification system
  const { showNotification, showConfirmation, NotificationComponent, ConfirmationComponent } = useNotifications();

  const [alternateArtPrefs, setAlternateArtPrefs] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const { prefs } = await loadAlternateArtDisplayContextFromProgress(API_URL, token);
        if (!cancelled) {
          setAlternateArtPrefs(prefs);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Strategy: failed to load alternate art prefs:', err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    void preloadAllOrderBorderImages();
  }, []);
  
  // Detect mobile viewport for responsive design - optimized for landscape mode
  // In landscape, check height (iPhone 12 Pro landscape: 844px × 390px)
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      const height = window.innerHeight;
      // For landscape mobile: check height (landscape phones have small heights)
      const isMobileByHeight = height <= 768;
      
      // Also check matchMedia if available (using height for landscape)
      let matchesMediaQuery = false;
      if (window.matchMedia) {
        const mediaQuery = window.matchMedia('(max-height: 768px)');
        matchesMediaQuery = mediaQuery.matches;
      }
      
      // Use matchMedia if available, otherwise use height check
      const isMobileResult = window.matchMedia ? matchesMediaQuery : isMobileByHeight;
      
      console.log('🔍 Initial mobile detection (landscape mode):', { 
        width, 
        height,
        isMobileByHeight,
        matchesMediaQuery,
        finalResult: isMobileResult
      });
      return isMobileResult;
    }
    return false;
  });
  
  useEffect(() => {
    const checkMobile = () => {
      if (typeof window === 'undefined') return;
      
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // For landscape mobile: check height (landscape phones have small heights)
      const isMobileByHeight = height <= 768;
      
      // Also check matchMedia if available (using height for landscape)
      let matchesMediaQuery = false;
      if (window.matchMedia) {
        const mediaQuery = window.matchMedia('(max-height: 768px)');
        matchesMediaQuery = mediaQuery.matches;
      }
      
      // Prefer matchMedia, fallback to height check
      const isMobileResult = window.matchMedia ? matchesMediaQuery : isMobileByHeight;
      
      console.log('🔍 Mobile check (landscape mode):', { 
        width, 
        height,
        isMobileByHeight,
        matchesMediaQuery,
        finalResult: isMobileResult
      });
      
      setIsMobile(isMobileResult);
    };
    
    // Check immediately
    checkMobile();
    
    // Use matchMedia listener if available (more reliable)
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(max-height: 768px)');
      
      const handleMediaChange = (e) => {
        console.log('🔍 Media query changed (height):', { matches: e.matches, height: window.innerHeight, width: window.innerWidth });
        setIsMobile(e.matches);
      };
      
      // Set initial state
      handleMediaChange(mediaQuery);
      
      // Listen for changes
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleMediaChange);
      } else {
        mediaQuery.addListener(handleMediaChange);
      }
      
      // Also listen to resize as backup
      window.addEventListener('resize', checkMobile);
      
      return () => {
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener('change', handleMediaChange);
        } else {
          mediaQuery.removeListener(handleMediaChange);
        }
        window.removeEventListener('resize', checkMobile);
      };
    } else {
      // Fallback: just use resize listener
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }
  }, []);
  
  // Sync with viewport scaling hook's mobile detection for consistency
  useEffect(() => {
    if (isContainerMobile !== isMobile) {
      setIsMobile(isContainerMobile);
    }
  }, [isContainerMobile]);
  
  // i18n is ready when language/resources exist; no per-render debug logging

	  const [searchQuery, setSearchQuery] = useState('');
	  const [orderCounts, setOrderCounts] = useState({});
      const [sidebarMessages, setSidebarMessages] = useState([]);
	  const [selectedFaction, setSelectedFaction] = useState('Remnants of Humanity');
      const [orders, setOrders] = useState([]);
      const [ordersCatalogLoading, setOrdersCatalogLoading] = useState(true);
      const [ordersCatalogError, setOrdersCatalogError] = useState(null);
      const [selectedCosts, setSelectedCosts] = useState([]);
      const [showStrategyStatisticsModal, setShowStrategyStatisticsModal] = useState(false);
      const [showSentients, setShowSentients] = useState(true);
      const [showOnlyOwned, setShowOnlyOwned] = useState(false);
  const [sortBy, setSortBy] = useState('cost'); // 'cost', 'rarity', 'name', 'type'
	  const [userStrategies, setUserStrategies] = useState([]);
	const [selectedStrategyId, setSelectedStrategyId] = useState('');

  const handleToggleAlternateArt = useCallback(
    async (orderId) => {
      const key = String(orderId);
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification(
          t('strategy.alerts.notLoggedIn', { defaultValue: 'You must be logged in to switch alternate art' }),
          'error'
        );
        return;
      }
      try {
        const res = await fetch(`${API_URL}/user/order-alternate-art`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ orderId: Number(orderId) }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          showNotification(
            err.error || t('strategy.alerts.alternateArtFailed', { defaultValue: 'Could not update alternate art' }),
            'error'
          );
          return;
        }
        const data = await res.json();
        const nextVariant = Number(data.artVariant);
        if (!Number.isInteger(nextVariant) || nextVariant < 0) {
          throw new Error('handleToggleAlternateArt: invalid artVariant in response');
        }
        const { prefs } = await loadAlternateArtDisplayContextFromProgress(API_URL, token);
        setAlternateArtPrefs(prefs);
        patchAlternateArtDisplayPref(orderId, nextVariant);
      } catch (err) {
        console.error('handleToggleAlternateArt failed:', err);
        showNotification(
          t('strategy.alerts.alternateArtFailed', { defaultValue: 'Could not update alternate art' }),
          'error'
        );
      }
    },
    [showNotification, t]
  );

	  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [modalStrategyName, setModalStrategyName] = useState('');
  const [modalDescription, setModalDescription] = useState('');
  const [modalIsPrivate, setModalIsPrivate] = useState(false);
  const [isDuplicateMode, setIsDuplicateMode] = useState(false);
  const [orderDuplicators, setOrderDuplicators] = useState({
    common: 0,
    rare: 0,
    epic: 0,
    legendary: 0
  });
  const [billyBucks, setBillyBucks] = useState(0);
  const [originalStrategyOrders, setOriginalStrategyOrders] = useState(null); // Track original orders for change detection
  const strategyJustLoadedRef = React.useRef(false); // Track if strategy was just loaded (no changes made yet) - use ref for immediate availability
  const strategySidebarDirtyRef = React.useRef(false); // User edited sidebar; blocks deferred reload from wiping edits

  const commitStrategyLoadBaseline = useCallback((strategyOrders) => {
    setOriginalStrategyOrders(
      (strategyOrders || []).map(({ orderId, order_id, count }) => ({
        orderId: orderId || order_id,
        count,
      }))
    );
    strategySidebarDirtyRef.current = false;
    strategyJustLoadedRef.current = true;
  }, []);

  const markStrategySidebarDirty = useCallback(() => {
    strategySidebarDirtyRef.current = true;
    strategyJustLoadedRef.current = false;
  }, []);

  useEffect(() => {
    registerStrategyDraftGetter(() => {
      const counts = {};
      (sidebarMessages || []).forEach(({ name, cost }) => {
        const order = (orders || []).find((o) => o.name === name && o.cost === cost);
        if (order) {
          counts[order.id] = (counts[order.id] ?? 0) + 1;
        }
      });
      const strategyOrders = Object.entries(counts).map(([orderId, count]) => ({
        orderId: parseInt(orderId, 10),
        count,
      }));
      if (strategyOrders.length === 0) {
        return null;
      }
      const draftName = `Draft ${new Date().toISOString().replace('T', ' ').slice(0, 19)}`;
      return {
        name: draftName,
        description: '',
        is_private: true,
        orders: strategyOrders,
      };
    });
    return () => unregisterStrategyDraftGetter();
  }, [sidebarMessages, orders]);

  const applyDevStrategyLoadData = useCallback(async (data) => {
    if (!data || !Array.isArray(data.orders)) {
      throw new Error('applyDevStrategyLoadData: invalid payload');
    }

    strategyLoadedByOnChange.current = true;
    strategySidebarDirtyRef.current = false;
    setMissingOrders([]);
    setStrategyValidation({ valid: true, unownedOrders: [] });

    const userStrategyId = data.userStrategyId != null ? String(data.userStrategyId) : '';
    setSelectedStrategyId(userStrategyId);

    if (data.userStrategyId != null) {
      const hasUnowned = data.orders.some(
        ({ isOwned, availability }) => availability !== 'Created' && isOwned === false
      );
      setUserStrategies((prev) => {
        const id = Number(data.userStrategyId);
        const entry = {
          id,
          name: data.name,
          orders: data.orders,
          faction_name: data.faction_name,
          hasUnownedOrders: hasUnowned,
        };
        const idx = prev.findIndex((s) => s.id === id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], ...entry };
          return next;
        }
        return [entry, ...prev];
      });
    }

    const newMessages = [];
    const newCounts = {};
    const missingOrderIds = [];

    data.orders.forEach(({ orderId, order_id, count, isOwned, availability, name, cost }) => {
      const id = orderId || order_id;
      if (!id) return;
      const owned = isOwned !== false || availability === 'Created';
      const countNum = typeof count === 'number' ? count : parseInt(count, 10);
      if (Number.isNaN(countNum) || countNum < 1) {
        throw new Error(`applyDevStrategyLoadData: invalid count for order ${id}`);
      }

      const order =
        (orders || []).find((o) => o.id === id) ||
        { id, name, cost, availability, faction_name: data.faction_name };

      if (!(orders || []).find((o) => o.id === id)) {
        missingOrderIds.push({ id, count: countNum, isOwned, availability, name, cost });
      }

      for (let i = 0; i < countNum; i++) {
        newMessages.push(createSidebarOrderEntry(order, owned));
      }
      newCounts[id] = countNum;
    });

    if (missingOrderIds.length > 0) {
      const fetched = await Promise.all(
        missingOrderIds.map(({ id }) =>
          fetch(`${API_URL}/order/${id}?${buildOrderDetailLangQuery(i18nInstance.language)}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          })
            .then((res) => (res.ok ? res.json() : null))
            .catch(() => null)
        )
      );
      const validFetched = fetched.filter(
        (o) => o && !(orders || []).find((existing) => existing.id === o.id)
      );
      setMissingOrders(validFetched);
    }

    newMessages.sort((a, b) => a.cost - b.cost);
    setSidebarMessages(newMessages);
    setOrderCounts(newCounts);
    commitStrategyLoadBaseline(data.orders);

    if (data.userStrategyId != null) {
      try {
        const validationRes = await fetch(`${API_URL}/strategy/${data.userStrategyId}/validate`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (validationRes.ok) {
          const validation = await validationRes.json();
          setStrategyValidation(validation);
        }
      } catch (err) {
        console.error('Dev strategy load validation failed:', err);
      }
    } else {
      const unownedOrders = data.orders.filter(
        (o) => o.availability !== 'Created' && o.isOwned === false
      );
      setStrategyValidation({
        valid: unownedOrders.length === 0,
        unownedOrders: unownedOrders.map((o) => ({ id: o.orderId || o.order_id, name: o.name })),
      });
    }

    strategyLoadedByOnChange.current = false;
    showNotification(
      `Loaded: ${data.name} (${data.orders.length} order types)`,
      'success'
    );
  }, [
    orders,
    commitStrategyLoadBaseline,
    i18nInstance.language,
    showNotification,
  ]);

  const handleDevLoadStrategyByKey = useCallback(async () => {
    const strategyKey = devStrategyKeyInput.trim().toLowerCase();
    if (!/^[a-f0-9]{64}$/.test(strategyKey)) {
      showNotification('Enter a 64-character hex strategy_key', 'warning');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      showNotification('Not logged in', 'error');
      return;
    }

    setDevStrategyKeyLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/dev/strategy-by-key?strategyKey=${encodeURIComponent(strategyKey)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!res.ok) {
        showNotification(data.error || 'Failed to load strategy by key', 'error');
        return;
      }

      pendingDevStrategyLoadRef.current = data;
      const targetFaction = data.faction_name;
      if (
        targetFaction &&
        targetFaction !== 'Unknown' &&
        targetFaction !== selectedFaction
      ) {
        setSelectedFaction(targetFaction);
        return;
      }

      pendingDevStrategyLoadRef.current = null;
      await applyDevStrategyLoadData(data);
    } catch (err) {
      console.error('Dev load strategy by key failed:', err);
      showNotification('Failed to load strategy by key', 'error');
    } finally {
      setDevStrategyKeyLoading(false);
    }
  }, [
    devStrategyKeyInput,
    selectedFaction,
    applyDevStrategyLoadData,
    showNotification,
  ]);

  const navigate = useNavigate();
  const { showStatTooltip, hideStatTooltip, statTooltipPortal } = useBattleStatTooltip();
  const [hoveredOrderId, setHoveredOrderId] = useState(null);
  const lastHoveredRef = useRef(null); // Track last hovered to avoid unnecessary updates
  const soundThrottleRef = useRef(0); // Throttle sound effects
  // Single state for sidebar tooltip position to avoid multiple re-renders on hover
  const [sidebarTooltipRect, setSidebarTooltipRect] = useState(null); // { index, top, bottom, left }
  const [strategyValidation, setStrategyValidation] = useState({ valid: true, unownedOrders: [] });
  const strategyLoadedByOnChange = React.useRef(false);
  const sidebarRef = React.useRef(null);
  const [factionTranslations, setFactionTranslations] = useState({}); // Map: english_name -> translated_name
  const [missingOrders, setMissingOrders] = useState([]); // Orders in strategy but not in current orders array
  const [showTutorialStrategy, setShowTutorialStrategy] = useState(false);
  const [tutorialStrategyStep, setTutorialStrategyStep] = useState(1);
  const { shouldShowTutorial, markSeenLocally } = useTutorialStatus(user);
  const { isReplayRequest: isReplayingStrategyTutorial } = useMenuTutorialReplay('strategy', {
    enabled: Boolean(user),
    onReplay: () => {
      setTutorialStrategyStep(1);
      setShowTutorialStrategy(true);
    },
  });
  const [strategyDeploySpawnCache, setStrategyDeploySpawnCache] = useState({});
  /** Viewport anchor for deploy-spawn card portal (escapes scaled / overflow scroll regions). */
  const [deploySpawnAnchorRect, setDeploySpawnAnchorRect] = useState(null);

  useEffect(() => {
    if (user && shouldShowTutorial('strategy')) {
      setTutorialStrategyStep(1);
      const timer = setTimeout(() => setShowTutorialStrategy(true), 600);
      return () => clearTimeout(timer);
    }
  }, [user, shouldShowTutorial]);

  // Stable hover handlers so memoized cards only re-render when isHovered changes (2 cards max)
  const handleCardHover = useCallback((order, event) => {
    if (Number(lastHoveredRef.current) !== Number(order.id)) {
      lastHoveredRef.current = order.id;
      setHoveredOrderId(order.id);
      const now = Date.now();
      if (now - soundThrottleRef.current > 100) {
        soundThrottleRef.current = now;
        playSoundEffect('/sounds/hoverover.mp3', 0.3);
      }
    }
    if (event?.currentTarget) {
      setDeploySpawnAnchorRect(event.currentTarget.getBoundingClientRect());
    }
  }, []);
  const handleCardHoverMove = useCallback((order, event) => {
    if (Number(lastHoveredRef.current) !== Number(order.id)) return;
    if (event?.currentTarget) {
      setDeploySpawnAnchorRect(event.currentTarget.getBoundingClientRect());
    }
  }, []);
  const handleCardHoverEnd = useCallback((order) => {
    if (Number(lastHoveredRef.current) !== Number(order.id)) return;
    lastHoveredRef.current = null;
    setHoveredOrderId(null);
    setDeploySpawnAnchorRect(null);
  }, []);

  const resolvedStrategyHoveredOrder = useMemo(() => {
    if (hoveredOrderId == null) return null;
    const raw = hoveredOrderId;
    let id;
    if (typeof raw === 'string' && raw.startsWith('missing-')) {
      id = parseInt(raw.slice('missing-'.length), 10);
    } else {
      id = Number(raw);
    }
    if (!Number.isFinite(id)) return null;
    const matchId = (o) => Number(o.id) === id;
    return (orders || []).find(matchId) || (missingOrders || []).find(matchId) || null;
  }, [hoveredOrderId, orders, missingOrders]);

  const strategyDeploySpawnInfo = useMemo(
    () => (resolvedStrategyHoveredOrder ? getDeployedUnitInfo(resolvedStrategyHoveredOrder.id) : null),
    [resolvedStrategyHoveredOrder]
  );

  const strategyStatisticsOrders = useMemo(
    () =>
      (sidebarMessages || []).map((msg) => {
        const resolved = resolveSidebarOrderRecord(msg, orders, missingOrders);
        if (resolved) {
          const typeOriginal = resolved.type_original ?? resolved.type;
          if (!typeOriginal) {
            throw new Error(
              `Strategy statistics: order ${resolved.id ?? msg.orderId ?? msg.name} is missing type`
            );
          }
          return resolved;
        }
        return {
          id: msg.orderId,
          name: msg.name,
          cost: msg.cost,
          rarity: 'common',
          type_original: 'Unit',
          type: 'Unit',
          faction_name: 'Unknown',
        };
      }),
    [sidebarMessages, orders, missingOrders]
  );

  useEffect(() => {
    if (!showStrategyStatisticsModal) {
      return undefined;
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        playSoundEffect('/sounds/click.mp3');
        setShowStrategyStatisticsModal(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showStrategyStatisticsModal]);

  const deploySpawnPreviewCard = useMemo(() => {
    if (selectedFaction === DEV_CREATED_CATALOG_FACTION) return null;
    if (!strategyDeploySpawnInfo || strategyDeploySpawnInfo.isRandom || strategyDeploySpawnInfo.id == null) return null;
    const sid = Number(strategyDeploySpawnInfo.id);
    if (!Number.isFinite(sid)) return null;
    const fetched = strategyDeploySpawnCache[sid];
    if (!fetched) return null;
    return withAlternateArtDisplayOrder({
      ...fetched,
      attack_rating: strategyDeploySpawnInfo.customStats?.attack ?? fetched.attack_rating,
      health: strategyDeploySpawnInfo.customStats?.health ?? fetched.health,
      abilities: strategyDeploySpawnInfo.customStats?.abilities ?? fetched.abilities,
    });
  }, [selectedFaction, strategyDeploySpawnInfo, strategyDeploySpawnCache]);

  const createdDevSourceParentOrderIds = useMemo(() => {
    if (selectedFaction !== DEV_CREATED_CATALOG_FACTION || !resolvedStrategyHoveredOrder) return [];
    return getSpawnSourceParentOrderIds(resolvedStrategyHoveredOrder.id);
  }, [selectedFaction, resolvedStrategyHoveredOrder]);

  const createdDevParentPreviewCards = useMemo(() => {
    if (!createdDevSourceParentOrderIds.length) return null;
    const cards = [];
    for (const pid of createdDevSourceParentOrderIds) {
      const row = strategyDeploySpawnCache[pid];
      if (!row) return null;
      cards.push(row);
    }
    return cards;
  }, [createdDevSourceParentOrderIds, strategyDeploySpawnCache]);

  const filteredStrategyGridOrders = useMemo(() => (orders || [])
    .filter(order => selectedCosts.length === 0 || selectedCosts.includes(order.cost))
    .filter(order => {
      if (selectedFaction === DEV_CREATED_CATALOG_FACTION) {
        return String(order.availability || '').toLowerCase() === 'created';
      }
      return order.availability !== 'Created';
    })
    .filter(order => {
      if (isDuplicateMode) {
        const rarity = (order.rarity || 'common').toLowerCase();
        const duplicatorCount = orderDuplicators[rarity] || 0;
        if (duplicatorCount <= 0) {
          return false;
        }
        const maxCount = order.is_unique === true ? 1 : 3;
        const ownedCount = order.ownedCount !== undefined ? order.ownedCount : (order.isOwned ? 999 : 0);
        if (ownedCount < 1) {
          return false;
        }
        if (ownedCount >= maxCount) {
          return false;
        }
        return true;
      }
      if (showOnlyOwned) {
        const ownedCount = order.ownedCount !== undefined ? order.ownedCount : (order.isOwned ? 999 : 0);
        const isOwned = ownedCount > 0 || order.availability === 'Created';
        if (!isOwned) return false;
      }
      return true;
    })
    .filter(order =>
      fuzzyMatch(order.name, searchQuery) ||
      fuzzyMatch(order.type, searchQuery) ||
      fuzzyMatch(order.description, searchQuery)
    )
    .sort((a, b) => {
      const normalize = name => (name ?? '').trim().toLowerCase();
      const aIsSentient = normalize(a.faction_name) === 'the sentients';
      const bIsSentient = normalize(b.faction_name) === 'the sentients';
      if (aIsSentient && !bIsSentient) return 1;
      if (!aIsSentient && bIsSentient) return -1;
      let primaryComparison = 0;
      switch (sortBy) {
        case 'cost':
          primaryComparison = (a.cost ?? 0) - (b.cost ?? 0);
          break;
        case 'rarity':
          primaryComparison = getRarityOrder(a.rarity) - getRarityOrder(b.rarity);
          break;
        case 'name':
          primaryComparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'type':
          primaryComparison = (a.type || '').localeCompare(b.type || '');
          break;
        default:
          primaryComparison = (a.cost ?? 0) - (b.cost ?? 0);
      }
      if (primaryComparison !== 0) return primaryComparison;
      if (sortBy !== 'name') {
        return (a.name || '').localeCompare(b.name || '');
      }
      return (a.cost ?? 0) - (b.cost ?? 0);
    }), [
    orders,
    selectedCosts,
    isDuplicateMode,
    orderDuplicators,
    showOnlyOwned,
    searchQuery,
    sortBy,
    selectedFaction,
  ]);

  useEffect(() => {
    if (selectedFaction !== DEV_CREATED_CATALOG_FACTION || createdDevSourceParentOrderIds.length === 0) return;
    const pending = createdDevSourceParentOrderIds.filter((pid) => !strategyDeploySpawnCache[pid]);
    if (pending.length === 0) return;
    let cancelled = false;
    const token = localStorage.getItem('token');
    Promise.all(
      pending.map((pid) =>
        fetch(`${API_URL}/order/${pid}?${buildOrderDetailLangQuery(i18nInstance.language)}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => ({ pid, data }))
      )
    )
      .then((results) => {
        if (cancelled) return;
        setStrategyDeploySpawnCache((prev) => {
          let next = prev;
          let changed = false;
          for (const { pid, data } of results) {
            if (!data || prev[pid]) continue;
            if (!changed) {
              next = { ...prev };
              changed = true;
            }
            next[pid] = data;
          }
          return changed ? next : prev;
        });
      })
      .catch((err) => console.warn('Strategy Created(dev) spawn-source preview fetch failed', err));
    return () => {
      cancelled = true;
    };
  }, [
    selectedFaction,
    createdDevSourceParentOrderIds,
    strategyDeploySpawnCache,
    i18nInstance.language,
  ]);

  useEffect(() => {
    if (selectedFaction === DEV_CREATED_CATALOG_FACTION) return;
    const info = strategyDeploySpawnInfo;
    const spawnId = info?.id != null ? Number(info.id) : NaN;
    if (!Number.isFinite(spawnId) || info?.isRandom) return;
    if (strategyDeploySpawnCache[spawnId]) return;
    let cancelled = false;
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/order/${spawnId}?${buildOrderDetailLangQuery(i18nInstance.language)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setStrategyDeploySpawnCache((prev) => (prev[spawnId] ? prev : { ...prev, [spawnId]: data }));
      })
      .catch((err) => console.warn('Strategy deploy spawn preview: order fetch failed', err));
    return () => {
      cancelled = true;
    };
  }, [selectedFaction, strategyDeploySpawnInfo, i18nInstance.language, strategyDeploySpawnCache]);

  const handleOrderClickRef = useRef(() => {});
  const handleOrderClickById = useCallback((orderId) => {
    const order = (orders || []).find(o => o.id === orderId);
    if (order) handleOrderClickRef.current(order);
  }, [orders]);

  const fetchOrdersListForCurrentFaction = useCallback(async () => {
    const token = localStorage.getItem('token');
    return fetchStrategyOrdersCatalog({
      token,
      selectedFaction,
      showSentients,
      language: i18nInstance.language,
    });
  }, [selectedFaction, showSentients, i18nInstance.language]);

  useEffect(() => {
    const getOrderPrice = (o) => {
      const r = (o.rarity || 'common').toLowerCase();
      return { common: 3000, rare: 6000, epic: 10000, legendary: 30000 }[r] || 3000;
    };
    handleOrderClickRef.current = async (order) => {
      const ownedCount = order.ownedCount !== undefined ? order.ownedCount : (order.isOwned ? 999 : 0);
      const uniquenessLimit = order.is_unique === true ? 1 : 3;
      const maxCount = order.availability === 'Created' ? uniquenessLimit : Math.min(ownedCount, uniquenessLimit);
      const currentCount = orderCounts[order.id] ?? 0;
      const isAtMaxCount = currentCount >= maxCount;
      const canPurchase = isAtMaxCount && order.availability !== 'Created' && ownedCount < uniquenessLimit;
      const orderPrice = getOrderPrice(order);
      const canAfford = billyBucks >= orderPrice;
      const isUnowned = (ownedCount === 0 || ownedCount === undefined) && order.availability !== 'Created';

      if (canPurchase && !isDuplicateMode) {
        if (!canAfford) {
          showNotification(t('bank.insufficientBucks') || 'Insufficient Billy Bucks', 'error');
          return;
        }
        try {
          playSoundEffect('/sounds/click.mp3');
          const response = await fetch(`${API_URL}/bank/purchase`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify({ orderId: order.id })
          });
          const data = await response.json();
          if (response.ok) {
            setBillyBucks(data.newBalance || 0);
            try {
              const ordersData = await fetchOrdersListForCurrentFaction();
              const updatedOrder = ordersData.find(o => o.id === order.id);
              if (updatedOrder) setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
            } catch (e) {
              console.error('Failed to refresh orders after purchase', e);
            }
            showNotification(data.message || 'Order purchased successfully', 'success');
          } else {
            showNotification(data.error || 'Failed to purchase order', 'error');
          }
        } catch (err) {
          showNotification('Failed to purchase order', 'error');
        }
        return;
      }

      if (isDuplicateMode) {
        const rarity = (order.rarity || 'common').toLowerCase();
        const duplicatorCount = orderDuplicators[rarity] || 0;
        const isUnique = order.is_unique === true;
        const dupMaxCount = isUnique ? 1 : 3;
        const dupCurrentCount = orderCounts[order.id] ?? 0;
        if (duplicatorCount <= 0) {
          showNotification(t('strategy.duplicate.errors.noDuplicator', { rarity: rarity.charAt(0).toUpperCase() + rarity.slice(1) }), 'error');
          return;
        }
        if (ownedCount < 1) {
          showNotification(t('strategy.duplicate.errors.mustOwn'), 'error');
          return;
        }
        if (dupCurrentCount >= dupMaxCount) {
          showNotification(t('strategy.duplicate.errors.maxReached', { max: dupMaxCount }), 'error');
          return;
        }
        try {
          playSoundEffect('/sounds/click.mp3');
          const response = await fetch(`${API_URL}/purchase-duplicate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify({ orderId: order.id })
          });
          const data = await response.json();
          if (response.ok) {
            const duplicatorResponse = await fetch(`${API_URL}/profile/consumables`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
            if (duplicatorResponse.ok) {
              const duplicatorData = await duplicatorResponse.json();
              setOrderDuplicators(duplicatorData.duplicators || { common: 0, rare: 0, epic: 0, legendary: 0 });
              setBillyBucks(duplicatorData.billyBucks || 0);
            }
            try {
              const ordersData = await fetchOrdersListForCurrentFaction();
              const updatedOrder = ordersData.find(o => o.id === order.id);
              if (updatedOrder) setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
            } catch (e) {
              console.error('Failed to refresh orders after duplicate purchase', e);
            }
            playSoundEffect('/sounds/add_to_strat.mp3');
            setSidebarMessages(prev => {
              const updated = [...prev, createSidebarOrderEntry(order, true)];
              updated.sort((a, b) => a.cost - b.cost);
              return updated;
            });
            setOrderCounts(prev => ({ ...prev, [String(order.id)]: (prev[String(order.id)] || 0) + 1 }));
            markStrategySidebarDirty();
          } else {
            showNotification(data.error || t('strategy.duplicate.errors.purchaseFailed'), 'error');
          }
        } catch (err) {
          showNotification(t('strategy.duplicate.errors.purchaseFailed'), 'error');
        }
        return;
      }

      if (canPurchase) return;
      if (isUnowned) return;
      if (isAtMaxCount) return;
      playSoundEffect('/sounds/add_to_strat.mp3');
      setSidebarMessages(prev => {
        const updated = [...prev, createSidebarOrderEntry(order, true)];
        updated.sort((a, b) => a.cost - b.cost);
        return updated;
      });
      setOrderCounts(prev => ({ ...prev, [String(order.id)]: (prev[String(order.id)] || 0) + 1 }));
      markStrategySidebarDirty();
    };
  }, [orderCounts, orderDuplicators, billyBucks, selectedFaction, i18nInstance.language, showNotification, playSoundEffect, t, isDuplicateMode, fetchOrdersListForCurrentFaction, markStrategySidebarDirty]);

  // Fetch duplicators and Billy Bucks on component mount
  useEffect(() => {
    const fetchDuplicators = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await fetch(`${API_URL}/profile/consumables`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setOrderDuplicators(data.duplicators || {
            common: 0,
            rare: 0,
            epic: 0,
            legendary: 0
          });
          setBillyBucks(data.billyBucks || 0);
        } else {
          console.error('Failed to fetch consumables:', response.status);
        }
      } catch (err) {
        console.error('Error fetching consumables:', err);
      }
    };
    
    fetchDuplicators();
  }, []);

  // Fetch faction translations
  useEffect(() => {
    const fetchFactionTranslations = async () => {
      try {
        const currentLanguage = i18nInstance.language || 'en-us';
        const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
        
        const response = await fetch(`${API_URL}/factions?lang=${encodeURIComponent(normalizedLanguage)}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const factions = await response.json();
          const translationMap = {};
          factions.forEach(faction => {
            translationMap[faction.english_name] = faction.name;
          });
          setFactionTranslations(translationMap);
        }
      } catch (err) {
        console.error('Failed to fetch faction translations:', err);
        // Fallback: use English names
        setFactionTranslations({});
      }
    };
    
    fetchFactionTranslations();
  }, [i18nInstance.language]);

  // Reset the flag when selectedStrategyId changes (but not when it's set by onChange)
  useEffect(() => {
    if (!selectedStrategyId) {
      strategyLoadedByOnChange.current = false;
    }
  }, [selectedStrategyId]);

  // Prevent sidebar scroll from propagating to main page
  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const handleWheel = (e) => {
      // Check if sidebar is scrollable and if we're at the boundaries
      const { scrollTop, scrollHeight, clientHeight } = sidebar;
      const isAtTop = scrollTop === 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
      const scrollingDown = e.deltaY > 0;
      const scrollingUp = e.deltaY < 0;

      // If scrolling down and at bottom, or scrolling up and at top, allow default (scroll main page)
      // Otherwise, prevent default (scroll sidebar only)
      if ((scrollingDown && isAtBottom) || (scrollingUp && isAtTop)) {
        // Allow scroll to propagate to main page only at boundaries
        return;
      }

      // Prevent scroll from propagating to main page
      e.stopPropagation();
      // Allow the sidebar to scroll naturally by not preventing default
    };

    sidebar.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      sidebar.removeEventListener('wheel', handleWheel);
    };
  }, []);

  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const handleSidebarScroll = () => {
      hideStatTooltip();
    };

    sidebar.addEventListener('scroll', handleSidebarScroll, { passive: true });
    return () => {
      sidebar.removeEventListener('scroll', handleSidebarScroll);
    };
  }, [hideStatTooltip]);

// ✅ Insert this block right below:
// Note: This useEffect handles the case where orders load after a strategy is selected
// The main loading logic is in the CustomDropdown onChange handler
useEffect(() => {
  // Skip if onChange handler is currently loading, or if no strategy selected
  if (
    !selectedStrategyId ||
    strategyLoadedByOnChange.current ||
    (sidebarMessages || []).length > 0 ||
    strategySidebarDirtyRef.current
  ) {
    return;
  }

  const selected = (userStrategies || []).find(s => s.id.toString() === selectedStrategyId);
  if (!selected || !Array.isArray(selected.orders)) return;

  // Only run when orders are loaded
  if (orders.length === 0) {
    return;
  }

  const newMessages = [];
  const newCounts = {};
  const missingOrderIds = [];

  selected.orders.forEach(({ orderId, order_id, count, isOwned, availability }) => {
    // Support both orderId (camelCase) and order_id (snake_case) for compatibility
    const id = orderId || order_id;
    if (!id) return;
    
    const order = (orders || []).find(o => o.id === id);
    if (order) {
      const owned = isOwned !== false || availability === 'Created';
      for (let i = 0; i < count; i++) {
        newMessages.push(createSidebarOrderEntry(order, owned));
      }
      newCounts[order.id] = count;
    } else {
      // Order not found in current orders array - mark as missing
      missingOrderIds.push({ id, count, isOwned, availability });
    }
  });

  // Fetch missing orders if any
  if (missingOrderIds.length > 0) {
    Promise.all(missingOrderIds.map(({ id }) =>
      fetch(`${API_URL}/order/${id}?${buildOrderDetailLangQuery(i18nInstance.language)}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }).then(res => res.ok ? res.json() : null).catch(() => null)
    )).then(fetchedOrders => {
      // Store missing orders for grid display (only those not in current orders array)
      const validFetchedOrders = fetchedOrders.filter(o => {
        if (!o) return false;
        // Only include if not already in orders array
        return !(orders || []).find(existingOrder => existingOrder.id === o.id);
      });
      setMissingOrders(validFetchedOrders);
      
      // Add fetched orders to sidebar
      fetchedOrders.forEach((order, index) => {
        if (order) {
          const { count, isOwned, availability } = missingOrderIds[index];
          const owned = isOwned !== false || availability === 'Created';
          for (let i = 0; i < count; i++) {
            newMessages.push(createSidebarOrderEntry(order, owned));
          }
          newCounts[order.id] = count;
        }
      });
      
      if (newMessages.length > 0) {
        newMessages.sort((a, b) => a.cost - b.cost);
        console.log("Sidebar messages to set (from useEffect - orders loaded after selection):", newMessages);
        setSidebarMessages(newMessages);
        setOrderCounts(newCounts);
        commitStrategyLoadBaseline(selected.orders);
      }
    });
  } else {
    // No missing orders, clear the state
    setMissingOrders([]);
    
    if (newMessages.length > 0) {
      // Sort by cost
      newMessages.sort((a, b) => a.cost - b.cost);
      console.log("Sidebar messages to set (from useEffect - orders loaded after selection):", newMessages);
      setSidebarMessages(newMessages);
      setOrderCounts(newCounts);
      commitStrategyLoadBaseline(selected.orders);
    }
  }
}, [orders, selectedStrategyId, userStrategies, (sidebarMessages || []).length, i18nInstance.language, commitStrategyLoadBaseline]); // Include all dependencies

// Dev load by strategy_key after faction switch (player1 only)
useEffect(() => {
  if (!pendingDevStrategyLoadRef.current || orders.length === 0) {
    return;
  }
  const data = pendingDevStrategyLoadRef.current;
  pendingDevStrategyLoadRef.current = null;
  applyDevStrategyLoadData(data).catch((err) => {
    console.error('Pending dev strategy load failed:', err);
    showNotification('Failed to load strategy by key', 'error');
  });
}, [orders, applyDevStrategyLoadData, showNotification]);

// Handle strategy loading after faction switch
const pendingStrategyLoadAfterFactionSwitch = React.useRef(null);
useEffect(() => {
  // If faction was switched and orders are now loaded, continue loading the strategy
  if (pendingStrategyLoadAfterFactionSwitch.current && orders.length > 0 && selectedStrategyId) {
    const { strategyId, selected } = pendingStrategyLoadAfterFactionSwitch.current;
    pendingStrategyLoadAfterFactionSwitch.current = null;
    
    console.log('🔵 Continuing strategy load after faction switch');
    // Re-trigger the onChange handler logic to complete the loading
    // We'll manually call the validation and loading logic
    strategyLoadedByOnChange.current = true;
    
    // Use a small delay to ensure orders are fully processed
    setTimeout(async () => {
      try {
        const validationRes = await fetch(`${API_URL}/strategy/${strategyId}/validate`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        const validation = await validationRes.json();
        setStrategyValidation(validation);
        
        const hasUnowned = selected.orders.some(({ orderId, order_id, isOwned, availability }) => {
          if (availability === 'Created') return false;
          return isOwned === false;
        });
        
        const newMessages = [];
        const newCounts = {};
        const missingOrderIds = [];
        
        selected.orders.forEach(({ orderId, order_id, count, isOwned, availability }) => {
          const id = orderId || order_id;
          if (!id) return;
          
          const order = (orders || []).find(o => o.id === id);
          if (order) {
            // Add ALL orders (owned and unowned) but mark unowned ones
            const owned = isOwned !== false || availability === 'Created';
            if (hasUnowned || !validation.valid) {
              // Include all orders when there are unowned ones
              for (let i = 0; i < count; i++) {
                newMessages.push(createSidebarOrderEntry(order, owned));
              }
              newCounts[id] = count;
            } else {
              // All owned case
              for (let i = 0; i < count; i++) {
                newMessages.push(createSidebarOrderEntry(order, true));
              }
              newCounts[id] = count;
            }
          } else {
            missingOrderIds.push({ id, count, isOwned, availability });
          }
        });
        
        // Fetch missing orders
        if (missingOrderIds.length > 0) {
          const fetchPromises = missingOrderIds.map(({ id }) =>
            fetch(`${API_URL}/order/${id}?${buildOrderDetailLangQuery(i18nInstance.language)}`, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            }).then(res => res.ok ? res.json() : null).catch(() => null)
          );
          
          const fetchedOrders = await Promise.all(fetchPromises);
          
          // Store missing orders for grid display (only those not in current orders array)
          const validFetchedOrders = fetchedOrders.filter(o => {
            if (!o) return false;
            // Only include if not already in orders array
            return !(orders || []).find(existingOrder => existingOrder.id === o.id);
          });
          setMissingOrders(validFetchedOrders);
          
          fetchedOrders.forEach((order, index) => {
            if (order) {
              const { count, isOwned, availability } = missingOrderIds[index];
              const owned = isOwned !== false || availability === 'Created';
              if (hasUnowned || !validation.valid) {
                // Include all orders when there are unowned ones
                for (let i = 0; i < count; i++) {
                  newMessages.push(createSidebarOrderEntry(order, owned));
                }
                newCounts[order.id] = count;
              } else {
                // All owned case
                for (let i = 0; i < count; i++) {
                  newMessages.push(createSidebarOrderEntry(order, true));
                }
                newCounts[order.id] = count;
              }
            }
          });
        } else {
          // No missing orders, clear the state
          setMissingOrders([]);
        }
        
        // Sort by cost
        newMessages.sort((a, b) => a.cost - b.cost);
        console.log('🔵 Setting sidebar messages (after faction switch):', newMessages.length);
  setSidebarMessages(newMessages);
  setOrderCounts(newCounts);
        commitStrategyLoadBaseline(selected.orders);
        strategyLoadedByOnChange.current = false;
      } catch (err) {
        console.error('Failed to load strategy after faction switch:', err);
        strategyLoadedByOnChange.current = false;
      }
    }, 200);
  }
}, [orders, selectedStrategyId, userStrategies, i18nInstance.language, commitStrategyLoadBaseline]);


      
useEffect(() => {
  if (!selectedFaction) {
    setOrdersCatalogLoading(false);
    return undefined;
  }

  let cancelled = false;
  setOrdersCatalogLoading(true);
  setOrdersCatalogError(null);

  const loadCatalog = async () => {
    try {
      const token = localStorage.getItem('token');
      const { orders: uniqueOrders } = await awaitStrategyOrdersCatalogWithMedia({
        token,
        selectedFaction,
        showSentients,
        language: i18nInstance.language,
      });
      if (cancelled) {
        return;
      }
      setOrders(uniqueOrders);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      if (!cancelled) {
        setOrdersCatalogError(err);
        setOrders([]);
      }
    } finally {
      if (!cancelled) {
        setOrdersCatalogLoading(false);
      }
    }
  };

  void loadCatalog();

  return () => {
    cancelled = true;
  };
}, [selectedFaction, showSentients, i18nInstance.language]);

useEffect(() => {
  let cancelled = false;

  const fetchUserStrategies = async () => {
    try {
      const currentLanguage = i18nInstance.language || 'en-us';
      const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');

      const response = await fetch(`${API_URL}/my-strategies?lang=${encodeURIComponent(normalizedLanguage)}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();

      if (!Array.isArray(data)) {
        throw new Error(`Unexpected my-strategies response format: ${typeof data}`);
      }

      if (cancelled) {
        return;
      }

      const strategiesWithWarnings = data.map((strategy) => {
        const hasUnowned = strategy.orders && strategy.orders.some(({ isOwned, availability }) => {
          if (availability === 'Created') return false;
          return isOwned === false;
        });
        return {
          ...strategy,
          hasUnownedOrders: hasUnowned,
        };
      });
      setUserStrategies(strategiesWithWarnings);
    } catch (err) {
      console.error('Failed to fetch strategies:', err);
      if (!cancelled) {
        setUserStrategies([]);
      }
    }
  };

  void fetchUserStrategies();

  return () => {
    cancelled = true;
  };
}, [i18nInstance.language]);

  // Clear missing orders when faction changes (unless strategy is being loaded)
  useEffect(() => {
    if (!strategyLoadedByOnChange.current && !pendingStrategyLoadAfterFactionSwitch.current) {
      setMissingOrders([]);
    }
  }, [selectedFaction]);

  // Helper function to check if there are unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    if (strategyLoadedByOnChange.current || pendingStrategyLoadAfterFactionSwitch.current) {
      return false;
    }

    if (!selectedStrategyId) {
      return (sidebarMessages || []).length > 0;
    }

    let baselineOrders = originalStrategyOrders;
    if (!baselineOrders?.length) {
      const selected = (userStrategies || []).find((s) => s.id.toString() === selectedStrategyId);
      if (!selected?.orders?.length) {
        return (sidebarMessages || []).length > 0;
      }
      baselineOrders = selected.orders.map(({ orderId, order_id, count }) => ({
        orderId: orderId || order_id,
        count,
      }));
    }

    const originalCounts = buildOriginalCountsFromStrategyOrders(baselineOrders);
    const originalTotal = Object.values(originalCounts).reduce((sum, n) => sum + n, 0);
    const currentTotal = (sidebarMessages || []).length;
    if (currentTotal !== originalTotal) {
      return true;
    }

    const sidebarCounts = buildOrderCountsFromSidebarMessages(sidebarMessages);
    const orderCountsNormalized = normalizeOrderCountsMap(orderCounts);
    const currentCounts =
      Object.keys(sidebarCounts).length > 0 ? sidebarCounts : orderCountsNormalized;

    const allOrderIds = new Set([...Object.keys(currentCounts), ...Object.keys(originalCounts)]);
    for (const orderId of allOrderIds) {
      if ((currentCounts[orderId] || 0) !== (originalCounts[orderId] || 0)) {
        return true;
      }
    }

    return false;
  }, [selectedStrategyId, originalStrategyOrders, sidebarMessages, orderCounts, userStrategies]);

  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  hasUnsavedChangesRef.current = hasUnsavedChanges;

  // BrowserRouter does not support useBlocker (data router only). Guard browser-back via popstate.
  useEffect(() => {
    const pushGuardState = () => {
      window.history.pushState({ strategyUnsavedGuard: true }, '', window.location.href);
    };

    pushGuardState();

    const handlePopState = () => {
      if (!hasUnsavedChangesRef.current()) {
        return;
      }
      pushGuardState();
      showConfirmation(t('strategy.alerts.unsavedChanges'), (confirmed) => {
        if (confirmed) {
          strategySidebarDirtyRef.current = false;
          window.history.back();
        }
      });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [showConfirmation, t]);

  // Handle Esc key for navigation with unsaved changes warning
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle ESC key
      if (event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27) {
        // Don't handle if:
        // 1. User is typing in an input/textarea
        // 2. A modal is open (modals should handle ESC themselves)
        const activeElement = document.activeElement;
        const isInputFocused = activeElement && (
          activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.isContentEditable
        );

        const hasOpenModal = document.querySelector('.modal-overlay, [role="dialog"], .mulligan-dialog, .counterstrike-dialog, .friends-modal, .chat-modal');

        if (isInputFocused || hasOpenModal || showSaveDialog) {
          // Let the input or modal handle ESC first
          return;
        }

        // Check for unsaved changes
        if (hasUnsavedChangesRef.current()) {
          event.preventDefault();
          event.stopPropagation();
          showConfirmation(t('strategy.alerts.unsavedChanges'), (confirmed) => {
            if (confirmed) {
              strategySidebarDirtyRef.current = false;
              playSoundEffect('/sounds/click.mp3');
              navigate('/welcome');
            }
          });
          return;
        }

        // Navigate back
        playSoundEffect('/sounds/click.mp3');
        navigate('/welcome');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigate, t, showSaveDialog, hasUnsavedChanges]);

  function handleOpenSaveDialog() {
    playSoundEffect('/sounds/click.mp3');
    
    // If a strategy is selected and there are changes, ask user
    if (selectedStrategyId && hasUnsavedChanges()) {
      showConfirmation(t('strategy.alerts.saveToExisting'), (saveToExisting) => {
        if (saveToExisting) {
          // Save to existing strategy
          handleSaveToExistingStrategy();
        } else {
          // User chose not to save to existing, continue with new strategy dialog
          setModalStrategyName('');
          setModalDescription('');
          setModalIsPrivate(false);
          setShowSaveDialog(true);
        }
      });
      return;
    }
    
    setModalStrategyName('');
    setModalDescription('');
    setModalIsPrivate(false);
    setShowSaveDialog(true);
  }

  function handleSaveToExistingStrategy() {
    const orderCount = (sidebarMessages || []).length;
    
    // Check if strategy has invalid order count and show warning
    if (orderCount < 50 || orderCount > 100) {
      let warningMessage = '';
      if (orderCount < 50) {
        warningMessage = t('strategy.alerts.minOrdersWarning');
      } else if (orderCount > 100) {
        warningMessage = t('strategy.alerts.maxOrdersWarning');
      }
      
      showConfirmation(warningMessage, (confirmed) => {
        if (!confirmed) {
          return; // User cancelled, don't save
        }
        // Continue with save
        continueSaveToExisting();
      });
      return;
    }
    
    continueSaveToExisting();
  }
  
  function continueSaveToExisting() {
    const orderCount = (sidebarMessages || []).length;
    const strategyOrders = [];
    const counts = {};
    (sidebarMessages || []).forEach(({ name, cost }) => {
      const order = (orders || []).find(o => o.name === name && o.cost === cost);
      if (order) {
        counts[order.id] = (counts[order.id] ?? 0) + 1;
      }
    });
    for (const [orderId, count] of Object.entries(counts)) {
      strategyOrders.push({ orderId: parseInt(orderId), count });
    }
    
    fetch(`${API_URL}/strategy/${selectedStrategyId}/orders`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`
      },
      body: JSON.stringify({
        orders: strategyOrders
      })
    })
      .then(async res => {
        const data = await res.json();
        if (res.ok) {
          showNotification(data.message ?? t('strategy.alerts.updated'), 'success');
          // Update original orders to reflect saved state
          setOriginalStrategyOrders(strategyOrders.map(({ orderId, count }) => ({ orderId, count })));
          strategyJustLoadedRef.current = true; // Strategy saved, no unsaved changes now
        } else {
          showNotification(data.error || t('strategy.alerts.updateFailed'), 'error');
        }
      })
      .catch(err => {
        showNotification(t('strategy.alerts.updateFailed'), 'error');
      });
  }

  function handleSaveStrategy() {
    if (!modalStrategyName.trim()) {
      showNotification(t('strategy.alerts.noName'), 'warning');
      return;
    }
    
    const orderCount = (sidebarMessages || []).length;
    
    // Check if strategy has invalid order count and show warning
    if (orderCount < 50 || orderCount > 100) {
      let warningMessage = '';
      if (orderCount < 50) {
        warningMessage = t('strategy.alerts.minOrdersWarning');
      } else if (orderCount > 100) {
        warningMessage = t('strategy.alerts.maxOrdersWarning');
      }
      
      showConfirmation(warningMessage, (confirmed) => {
        if (!confirmed) {
          return; // User cancelled, don't save
        }
        // Continue with save
        continueSaveStrategy();
      });
      return;
    }
    
    continueSaveStrategy();
  }
  
  function continueSaveStrategy() {
    const orderCount = (sidebarMessages || []).length;
    const strategyOrders = [];
    const counts = {};
    (sidebarMessages || []).forEach(({ name, cost }) => {
      const order = (orders || []).find(o => o.name === name && o.cost === cost);
      if (order) {
        counts[order.id] = (counts[order.id] ?? 0) + 1;
      }
    });
    for (const [orderId, count] of Object.entries(counts)) {
      strategyOrders.push({ orderId: parseInt(orderId), count });
    }
    fetch(`${API_URL}/save-strategy`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`
      },
      body: JSON.stringify({
        name: modalStrategyName,
        description: modalDescription,
        is_private: modalIsPrivate,
        orders: strategyOrders
      })
    })
      .then(res => res.json())
      .then(data => {
        showNotification(data.message ?? t('strategy.alerts.saved'), 'success');
        setShowSaveDialog(false);
        // Update original orders if this was saved as a new strategy
        setOriginalStrategyOrders(null);
        setSelectedStrategyId('');
        strategyJustLoadedRef.current = false; // New strategy, clear flag
        // Refresh strategies list
        fetch(`${API_URL}/my-strategies?lang=${encodeURIComponent(i18nInstance.language || 'en-us')}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        })
          .then(res => res.json())
          .then(data => {
            if (Array.isArray(data)) {
              setUserStrategies(data);
            }
          })
          .catch(err => console.error("Failed to refresh strategies:", err));
      })
      .catch(err => {
        showNotification(t('strategy.alerts.saveFailed'), 'error');
      });
  }   

  
            // Debug: Log mobile state before rendering
            if (typeof window !== 'undefined') {
              console.log('🎨 Rendering Strategy:', { 
                isMobile, 
                windowWidth: window.innerWidth,
                hasMobileClass: isMobile ? 'YES' : 'NO'
              });
            }
            
            return (
            <div className="strategy-viewport-wrapper">
              {/* Full-screen background - outside viewport-container to cover entire screen */}
              <div 
                className="strategy-fullscreen-background"
                style={{
                  backgroundImage: resolvePublicAssetBackgroundUrl('/images/strategy.jpg'),
                }}
              />
              
              {/* Viewport container - scales to fit screen */}
              <div 
                className="strategy-viewport-container"
                style={{
                  width: `${containerBaseWidth}px`,
                  height: `${containerBaseHeight}px`,
                  transformOrigin: '0 0',
                  transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
                }}
              >
                <div 
                  className={`strategy-container${isMobile ? ' mobile' : ''}`}
                  data-width={typeof window !== 'undefined' ? window.innerWidth : 'unknown'}
                >
<MenuBackButton
  onClick={() => {
    if (hasUnsavedChangesRef.current()) {
      showConfirmation(t('strategy.alerts.unsavedChanges'), (confirmed) => {
        if (confirmed) {
          strategySidebarDirtyRef.current = false;
          navigate('/welcome');
        }
      });
      return;
    }
    navigate('/welcome');
  }}
/>

            <div className="strategy-main">
              <img 
                src="/images/strategy.jpg" 
                alt="Background" 
                className="strategy-background-image"
              />
            {/* Fixed Header Section - stays at top, no scrolling through */}
            <div className="strategy-header-fixed">
            {user && (
              <div className="strategy-header-user-controls">
                <FriendsIcon />
                <SettingsMenu onLogout={() => {
                  playSoundEffect('/sounds/click.mp3');
                  onLogout();
                  navigate('/');
                }} />
                <span style={{ color: 'white', marginRight: '10px' }}>{user}</span>
              </div>
            )}
            <div className="strategy-faction-bar">
            <div className="strategy-faction-icons">
  {allFactions.map(faction => {
  const isCombined = combinedFactions[faction];
  const color = isCombined ? factionColors['Combined'] : factionColors[faction] || '#ccc';
  const isSelected = selectedFaction === faction;

  let computedBackground = color;

  if (faction === 'The Conspiracy') {
    computedBackground = 'linear-gradient(to right, #8B0000 50%, #1A1A1A 50%)';
  } else if (faction === 'The Corrupted Core') {
    computedBackground = 'linear-gradient(to right, #4B0082 50%, #8B0000 50%)';
  } else if (faction === 'The Covenant') {
    computedBackground = 'linear-gradient(to right, #F4A460 50%, #1A1A1A 50%)';
  } else if (faction === 'The Divine Syndicate') {
    computedBackground = 'linear-gradient(to right, #F4A460 50%, #4B0082 50%)';
  } else if (faction === 'The Forsaken Pact') {
    computedBackground = 'linear-gradient(to right, #556B2F 50%, #8B0000 50%)';
  } else if (faction === 'The Hollow') {
    computedBackground = 'linear-gradient(to right, #F4A460 50%, #8B0000 50%)';
  } else if (faction === 'The Iron Alliance') {
    computedBackground = 'linear-gradient(to right, #556B2F 50%, #4B0082 50%)';
  } else if (faction === 'The Last Crusade') {
    computedBackground = 'linear-gradient(to right, #F4A460 50%, #556B2F 50%)';
  } else if (faction === 'The Shadow Consortium') {
    computedBackground = 'linear-gradient(to right, #1A1A1A 50%, #4B0082 50%)';
  } else if (faction === 'The Silent Accord') {
    computedBackground = 'linear-gradient(to right, #556B2F 50%, #1A1A1A 50%)';
  }

  const translatedFactionName = factionTranslations[faction] || faction;
  
  const factionIconPath = getFactionIconPath(faction);
  return (
    <div
      key={faction}
      onClick={() => {
        playSoundEffect('/sounds/click.mp3');
        setSelectedFaction(faction);
      }}
      className={`faction-icon ${isSelected ? 'selected' : ''}`}
      style={{ background: computedBackground }}
      aria-label={translatedFactionName}
      onMouseEnter={(event) => showStatTooltip(event, translatedFactionName, { placement: 'above' })}
      onMouseLeave={hideStatTooltip}
    >
      {factionIconPath && (
        <img
          src={factionIconPath}
          alt=""
          className="faction-icon-symbol"
          draggable={false}
          aria-hidden="true"
        />
      )}
    </div>
  );
})}

{showDebugOrderIds && (
  <div
    key={DEV_CREATED_CATALOG_FACTION}
    onClick={() => {
      playSoundEffect('/sounds/click.mp3');
      setSelectedFaction(DEV_CREATED_CATALOG_FACTION);
    }}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        playSoundEffect('/sounds/click.mp3');
        setSelectedFaction(DEV_CREATED_CATALOG_FACTION);
      }
    }}
    className={`faction-icon faction-icon-dev-created-catalog ${selectedFaction === DEV_CREATED_CATALOG_FACTION ? 'selected' : ''}`}
    role="button"
    tabIndex={0}
    aria-label="Dev: load catalog of orders with availability Created"
    onMouseEnter={(event) =>
      showStatTooltip(event, 'Created — all orders with availability Created (dev)', { placement: 'above' })
    }
    onMouseLeave={hideStatTooltip}
  />
)}

                          </div>
                          
                          <div className="cost-graph-wrapper">
<div className="cost-bar-chart-block">
<button
  type="button"
  className="strategy-statistics-details-btn"
  disabled={(sidebarMessages || []).length === 0}
  onClick={() => {
    playSoundEffect('/sounds/click.mp3');
    setShowStrategyStatisticsModal(true);
  }}
  aria-label={t('strategy.statisticsDetails', { defaultValue: 'Details' })}
>
  {t('strategy.statisticsDetails', { defaultValue: 'Details' })}
</button>
{(() => {
  const costCounts = [...Array(13).keys()].map((i) =>
    (sidebarMessages || []).filter((msg) => msg.cost === i).length
  );
  const maxCount = Math.max(...costCounts, 1);
  const maxBarHeight = 60;

  return (
    <>
      <div className="cost-bar-container">
        {[...Array(13).keys()].map((cost) => {
          const count = costCounts[cost];
          const barHeight = maxCount > 0 ? (count / maxCount) * maxBarHeight : 0;
          return (
            <div key={cost} className="cost-bar-segment">
              <div
                className="cost-bar-fill strategy-cost-bar-fill"
                style={{ height: `${barHeight}px` }}
                onMouseEnter={(event) =>
                  showStatTooltip(
                    event,
                    t('strategy.statistics.costTooltip', {
                      cost,
                      count,
                      defaultValue: `Cost ${cost}: ${count} orders`,
                    }),
                    { placement: 'above' }
                  )
                }
                onMouseLeave={hideStatTooltip}
              />
            </div>
          );
        })}
      </div>
      <div className="cost-bar-labels-row" aria-hidden="true">
        {[...Array(13).keys()].map((cost) => (
          <span key={cost} className="cost-bar-label">
            {cost}
          </span>
        ))}
      </div>
    </>
  );
})()}
</div>
<div className="cost-circle-container">
  {[...Array(13).keys()].map(i => {
    const cost = i + 0;
    const isActive = selectedCosts.includes(cost);
    return (
      <div
        key={cost}
        className={`cost-circle ${isActive ? 'active' : ''}`}
        onClick={() => {
          playSoundEffect('/sounds/click.mp3');
          setSelectedCosts(prev =>
            prev.includes(cost) ? prev.filter(c => c !== cost) : [...prev, cost]
          );
        }}
      >
        {cost}
      </div>
    );
  })}
</div>
<div style={{ 
  marginTop: '8px', 
  color: 'white', 
  fontSize: '0.9em', 
  display: 'flex', 
  alignItems: 'center', 
  gap: '5px' 
}}>
  <img 
    src="/images/billy_bucks.png" 
    alt="Billy Bucks" 
    style={{ width: '20px', height: '20px' }}
  />
  <span>{billyBucks.toLocaleString()}</span>
</div>



        
                      </div>
                      </div>
                      
                      <div className="strategy-selected-faction">
                    {selectedFaction && (
                      selectedFaction === DEV_CREATED_CATALOG_FACTION
                        ? 'Created (dev)'
                        : (factionTranslations[selectedFaction] || selectedFaction)
                    )}
                    </div>
   
<div style={{ margin: '8px 0 5px 0', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
  <input
    type="text"
    className="strategy-picklist"
    placeholder={isMobile ? t('strategy.searchPlaceholderMobile') : t('strategy.searchPlaceholder')}
    value={searchQuery}
    onChange={e => setSearchQuery(e.target.value)}
    style={{
      flex: '1',
      maxWidth: '50%',
      fontSize: '0.9em',
      padding: '6px',
      boxSizing: 'border-box'
    }}
  />
  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'white', fontSize: '0.9em', whiteSpace: 'nowrap' }}>
    {t('strategy.sortBy') || 'Sort by:'}
    <div style={{ minWidth: '150px', maxWidth: '150px' }}>
      <CustomDropdown
        options={[
          { value: 'cost', label: t('strategy.sort.cost') || 'Cost' },
          { value: 'rarity', label: t('strategy.sort.rarity') || 'Rarity' },
          { value: 'name', label: t('strategy.sort.name') || 'Name' },
          { value: 'type', label: t('strategy.sort.type') || 'Type' }
        ]}
        value={sortBy}
        onChange={(value) => {
          playSoundEffect('/sounds/click.mp3');
          setSortBy(value);
        }}
        placeholder={t('strategy.sortBy') || 'Sort by:'}
      />
    </div>
  </label>
  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'white', fontSize: '0.9em', cursor: 'pointer', whiteSpace: 'nowrap' }}>
    <input
      type="checkbox"
      checked={showOnlyOwned}
      onChange={e => {
        playSoundEffect('/sounds/click.mp3');
        setShowOnlyOwned(e.target.checked);
      }}
      style={{ cursor: 'pointer' }}
    />
    {t('strategy.onlyShowOwned')}
  </label>
  {isDuplicateMode ? (
    <button
      onClick={() => {
        playSoundEffect('/sounds/click.mp3');
        setIsDuplicateMode(false);
      }}
      style={{
        padding: '6px 12px',
        fontSize: '0.9em',
        backgroundColor: '#ff6b6b',
        color: 'white',
        border: 'none',
        cursor: 'pointer',
        borderRadius: '4px',
        whiteSpace: 'nowrap'
      }}
    >
      {t('strategy.duplicate.cancel')}
    </button>
  ) : (
    <button
      onClick={() => {
        playSoundEffect('/sounds/click.mp3');
        setIsDuplicateMode(true);
      }}
      style={{
        padding: '6px 12px',
        fontSize: '0.9em',
        backgroundColor: '#666',
        color: 'white',
        border: 'none',
        cursor: 'pointer',
        borderRadius: '4px',
        whiteSpace: 'nowrap'
      }}
    >
      {t('strategy.duplicate.button')}
    </button>
  )}
  {isDuplicateMode && (
    <div style={{ color: 'white', fontSize: '0.9em', fontStyle: 'italic', width: '100%', marginTop: '5px' }}>
      {t('strategy.duplicate.instruction')}
    </div>
  )}
</div>
</div>
{/* End Fixed Header Section */}

            {/* Scrollable Content Area */}
            <div className="strategy-content-scrollable">
              {ordersCatalogLoading ? (
                <div className="strategy-orders-catalog-loading" aria-busy="true">
                  <p>{t('strategy.loadingOrders', { defaultValue: 'Loading orders…' })}</p>
                </div>
              ) : ordersCatalogError ? (
                <div className="strategy-orders-catalog-error" role="alert">
                  <p>{t('strategy.loadingOrdersFailed', { defaultValue: 'Failed to load orders for this faction.' })}</p>
                </div>
              ) : (
 <div className="strategy-orders-grid">
  
{filteredStrategyGridOrders.map(order => {

      const color = combinedFactions[order.faction_name]
        ? factionColors['Combined']
        : factionColors[order.faction_name] || '#FFFFFF';

      // Use ownedCount if available, fallback to isOwned for backward compatibility
      const ownedCount = order.ownedCount !== undefined ? order.ownedCount : (order.isOwned ? 999 : 0);
      const isUnowned = (ownedCount === 0 || ownedCount === undefined) && order.availability !== 'Created';
      
      // Calculate max count: limit by both ownership and uniqueness
      // If availability is 'Created', player has unlimited (use uniqueness limit)
      // Otherwise, limit by ownedCount (capped by uniqueness limit)
      const uniquenessLimit = order.is_unique === true ? 1 : 3;
      const maxCount = order.availability === 'Created' 
        ? uniquenessLimit  // Created orders: can add up to uniqueness limit
        : Math.min(ownedCount, uniquenessLimit);  // Owned orders: can only add as many as owned (capped by uniqueness)
      const currentCount = orderCounts[order.id] ?? 0;
      const isAtMaxCount = currentCount >= maxCount;
      
      // Gray out if unowned OR if at max count
      const shouldGrayOut = isUnowned || isAtMaxCount;
      
      // For display: use actual ownedCount (capped at maxCount for display purposes)
      // If availability is 'Created', show maxCount/maxCount (player has unlimited)
      const displayOwnedCount = order.availability === 'Created' ? maxCount : Math.min(ownedCount, maxCount);
      
      // Display format: unassigned/total
      // First number: how many copies are currently unassigned (not in strategy sidebar)
      // Second number: how many you own in total
      const unassignedCount = order.availability === 'Created'
        ? Math.max(0, maxCount - currentCount)
        : Math.max(0, ownedCount - currentCount);
      const totalCount = order.availability === 'Created' ? maxCount : ownedCount;
      
      // Check if order can be purchased:
      // 1. Order must be greyed out because it's at max count in strategy (isAtMaxCount)
      // 2. User doesn't own enough copies to add more (ownedCount < uniquenessLimit)
      // 3. Order is not 'Created' (which means unlimited)
      // Note: We check isAtMaxCount (not shouldGrayOut) because we only want to purchase when
      // the order is greyed out due to being at max in strategy, not because it's unowned
      const canPurchase = isAtMaxCount && order.availability !== 'Created' && ownedCount < uniquenessLimit;
      
      return (
        <StrategyOrderCard
          key={order.id}
          order={order}
          isHovered={strategyOrdersMatchHover(hoveredOrderId, order.id)}
          onHover={handleCardHover}
          onHoverMove={handleCardHoverMove}
          onHoverEnd={handleCardHoverEnd}
          onOrderClickId={handleOrderClickById}
          orderCount={currentCount}
          displayOwnedCount={displayOwnedCount}
          maxCount={maxCount}
          canPurchase={canPurchase}
          isDuplicateMode={isDuplicateMode}
          shouldGrayOut={shouldGrayOut}
          ownedCount={ownedCount}
          isMobile={isMobile}
          unassignedCount={unassignedCount}
          totalCount={totalCount}
          showDebugOrderId={showDebugOrderIds}
          playUnitSoundLabel={playUnitSoundLabel}
          canSwitchAlternateArt={isAlternateArtUnlocked(order.id)}
          artVariant={getOrderArtVariant(order.id)}
          onToggleAlternateArt={handleToggleAlternateArt}
          switchAlternateArtLabel={switchAlternateArtLabel}
        />
      );
    })}
  
{/* Display missing orders from strategy (orders not in current faction or insufficiently owned) */}
{(missingOrders || [])
    .filter(order => {
      // Don't show if already in regular grid (to avoid duplicates)
      const isInRegularGrid = (orders || []).some(o => o.id === order.id);
      return !isInRegularGrid;
    })
    .filter(order => selectedCosts.length === 0 || selectedCosts.includes(order.cost))
    .filter(order =>
      fuzzyMatch(order.name, searchQuery) ||
      fuzzyMatch(order.type, searchQuery) ||
      fuzzyMatch(order.description, searchQuery)
    )
    .map(order => {
      const color = combinedFactions[order.faction_name]
        ? factionColors['Combined']
        : factionColors[order.faction_name] || '#FFFFFF';
      
      const currentCount = orderCounts[order.id] ?? 0;
      const isMissing = true; // These are missing orders
      const showUnitSound = strategyOrderShowsUnitSound(order);
      
      return (
        <div
          key={`missing-${order.id}`}
          onMouseEnter={(e) => {
            const missingId = `missing-${order.id}`;
            const now = Date.now();
            const timeSinceLastSound = now - soundThrottleRef.current;
            
            if (lastHoveredRef.current !== missingId) {
              lastHoveredRef.current = missingId;
              setHoveredOrderId(missingId);
              
              // Throttle sound: only play if 100ms have passed since last sound
              if (timeSinceLastSound > 100) {
                soundThrottleRef.current = now;
                playSoundEffect('/sounds/hoverover.mp3', 0.3);
              }
            }
            if (e.currentTarget) {
              setDeploySpawnAnchorRect(e.currentTarget.getBoundingClientRect());
            }
          }}
          onMouseMove={(e) => {
            if (lastHoveredRef.current === `missing-${order.id}` && e.currentTarget) {
              setDeploySpawnAnchorRect(e.currentTarget.getBoundingClientRect());
            }
          }}
          onMouseLeave={() => {
            const missingId = `missing-${order.id}`;
            if (lastHoveredRef.current === missingId) {
              lastHoveredRef.current = null;
              setHoveredOrderId(null);
              setDeploySpawnAnchorRect(null);
            }
          }}
          className={`strategy-order-card order-unowned ${!isMobile && strategyOrdersMatchHover(hoveredOrderId, order.id) ? 'strategy-order-hovered' : ''} ${isMobile ? 'mobile-order' : ''} tooltip-multiline`}
          style={{
            willChange: !isMobile ? 'transform' : 'auto',
            cursor: 'not-allowed',
            position: 'relative'
          }}
          data-mobile-order={isMobile}
          data-tooltip="This order is in your strategy but either not available in the current faction or you don't own enough copies"
        >
          {showUnitSound && (
            <StrategyOrderSpeakerButton orderId={order.id} ariaLabel={playUnitSoundLabel} />
          )}
          <div style={{
            filter: 'grayscale(100%)',
            pointerEvents: 'auto',
            position: 'relative'
          }}>
            <OrderDesign
              order={withAlternateArtDisplayOrder(order)}
              count={currentCount}
              ownedCount={0}
              minimal={false}
              lazyLoad={false}
              showOwnedCounter={false}
              maxCount={0}
              rarity={order.rarity}
              onClick={() => {
                // Prevent clicking missing orders
                showNotification('This order is not available in the current faction', 'warning');
              }}
            />
            {showDebugOrderIds && (
              <span className="strategy-order-id-debug">
                ID: {order.id}
              </span>
            )}
          </div>
          {/* Warning: top-right when speaker is shown (avoids overlap), else top-left */}
          <span style={{
            position: 'absolute',
            top: '10px',
            ...(showUnitSound ? { right: '10px', left: 'auto' } : { left: '10px', right: 'auto' }),
            color: 'orange',
            fontSize: '1.5em',
            fontWeight: 'bold',
            filter: 'none',
            zIndex: 10003,
            textShadow: '0 0 5px rgba(255, 165, 0, 0.8), 0 0 10px rgba(255, 165, 0, 0.6)'
          }}>⚠️</span>
        </div>
      );
    })}
</div>
              )}
</div>
{/* End Scrollable Content Area */}

    </div>

<div className="strategy-sidebar" ref={sidebarRef}>

<div style={{ marginBottom: "10px" }}>

<div style={{ marginTop: "10px" }}>
  {selectedStrategyId && !strategyValidation.valid && (
    <>
      <span
        className="tooltip-multiline"
        style={{ color: 'orange', marginLeft: '10px' }}
        data-tooltip={`This strategy contains orders you don't own: ${(strategyValidation.unownedOrders || []).map(o => o.name).join(', ')}`}
      >
        ⚠️
      </span>
      <div style={{ color: 'orange', fontSize: '0.9em', marginTop: '5px', marginBottom: '5px' }}>
        This strategy contains orders you don't own. Please remove them before playing.
      </div>
    </>
  )}
<CustomDropdown
  options={(userStrategies || []).map(s => ({
    value: s.id.toString(),
    label: s.name,
    hasWarning: s.hasUnownedOrders,
    faction_name: s.faction_name
  }))}
  value={selectedStrategyId}
  placeholder={t('strategy.selectStrategy')}
  renderOption={(option) => {
    const strategy = (userStrategies || []).find(s => s.id.toString() === option.value);
    const factionName = strategy?.faction_name || option.faction_name;
    if (!factionName) {
      return <span>{option.label} {option.hasWarning ? '⚠️' : ''}</span>;
    }
    const translatedFactionName = strategy?.faction_name_translated || factionTranslations[factionName] || factionName;

    const factionIconPath = getFactionIconPath(factionName);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div
          className="faction-orb strategy-sidebar-faction-orb"
          style={{ background: getFactionOrbBackground(factionName) }}
          onMouseEnter={(event) => showStatTooltip(event, translatedFactionName, { placement: 'above' })}
          onMouseLeave={hideStatTooltip}
        >
          {factionIconPath && (
            <img
              src={factionIconPath}
              alt=""
              className="faction-orb-icon"
              draggable={false}
              aria-hidden="true"
            />
          )}
        </div>
        <span>{option.label} {option.hasWarning ? '⚠️' : ''}</span>
      </div>
    );
  }}
  onChange={async (strategyId) => {
    pendingDevStrategyLoadRef.current = null;
    console.log('🔵 CustomDropdown onChange called with strategyId:', strategyId);
    // Set flag FIRST before any state updates to prevent useEffect from interfering
    strategyLoadedByOnChange.current = true;
    
    setSelectedStrategyId(strategyId);
    setSidebarMessages([]);
    setOrderCounts({});
    setStrategyValidation({ valid: true, unownedOrders: [] }); // Reset validation
    setOriginalStrategyOrders(null); // Reset original orders
    setMissingOrders([]); // Clear missing orders
    strategySidebarDirtyRef.current = false;
    strategyJustLoadedRef.current = false;
    
    if (!strategyId) {
      console.log('🔵 No strategyId, resetting');
      strategyLoadedByOnChange.current = false;
      return; // If deselecting, just reset
    }

    const selected = userStrategies.find(s => s.id.toString() === strategyId);
    console.log('🔵 Found strategy:', selected ? selected.name : 'NOT FOUND');
    if (!selected || !Array.isArray(selected.orders)) {
      console.log('🔵 Strategy not found or no orders array');
      strategyLoadedByOnChange.current = false; // Reset if we can't load
      return;
    }

    console.log('🔵 Strategy has', selected.orders.length, 'orders');
    console.log('🔵 Available orders count:', orders.length);
    
    // Determine the primary faction(s) from the strategy's orders
    // First, try to get faction_name from orders (might need to fetch if not available)
    const strategyFactions = new Set();
    const fetchedOrdersForFaction = [];
    
    // Collect faction names from orders that are already loaded
    selected.orders.forEach(({ orderId, order_id }) => {
      const id = orderId || order_id;
      if (!id) return;
      
      const order = (orders || []).find(o => o.id === id);
      if (order && order.faction_name) {
        strategyFactions.add(order.faction_name);
      } else {
        // Need to fetch this order to get its faction
        fetchedOrdersForFaction.push(id);
      }
    });
    
    // Fetch missing orders to determine their factions
    if (fetchedOrdersForFaction.length > 0) {
      console.log('🔵 Fetching', fetchedOrdersForFaction.length, 'orders to determine factions');
      const fetchPromises = fetchedOrdersForFaction.map(id =>
        fetch(`${API_URL}/order/${id}?${buildOrderDetailLangQuery(i18nInstance.language)}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }).then(res => res.ok ? res.json() : null).catch(() => null)
      );
      
      const fetchedOrders = await Promise.all(fetchPromises);
      fetchedOrders.forEach(order => {
        if (order && order.faction_name) {
          strategyFactions.add(order.faction_name);
        }
      });
    }
    
    // Determine which faction to select
    const factionArray = Array.from(strategyFactions);
    console.log('🔵 Strategy uses factions:', factionArray);
    
    if (factionArray.length > 0) {
      // First, count all factions (including from fetched orders) to get accurate counts
      const factionCounts = {};
      
      // Count from already loaded orders
      selected.orders.forEach(({ orderId, order_id }) => {
        const id = orderId || order_id;
        if (!id) return;
        
        const order = (orders || []).find(o => o.id === id);
        if (order && order.faction_name) {
          factionCounts[order.faction_name] = (factionCounts[order.faction_name] || 0) + 1;
        }
      });
      
      // Also count from fetched orders (for faction detection)
      if (fetchedOrdersForFaction.length > 0) {
        const fetchPromises = fetchedOrdersForFaction.map(id =>
          fetch(`${API_URL}/order/${id}?${buildOrderDetailLangQuery(i18nInstance.language)}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }).then(res => res.ok ? res.json() : null).catch(() => null)
        );
        
        const fetchedOrders = await Promise.all(fetchPromises);
        fetchedOrders.forEach(order => {
          if (order && order.faction_name) {
            factionCounts[order.faction_name] = (factionCounts[order.faction_name] || 0) + 1;
            // Also add to strategyFactions set
            strategyFactions.add(order.faction_name);
          }
        });
      }
      
      // Update factionArray with all factions (including from fetched orders)
      const allStrategyFactions = Array.from(strategyFactions);
      console.log('🔵 All strategy factions (after fetching):', allStrategyFactions);
      console.log('🔵 Faction counts:', factionCounts);
      
      // Check if it matches a combined faction
      // Combined factions have: [combinedName, baseFaction1, baseFaction2, 'The Sentients']
      // We need to match the base factions (excluding Sentients and the combined name itself)
      let matchedFaction = null;
      let bestMatch = null;
      let bestMatchScore = 0;
      
      for (const [combinedFaction, componentFactions] of Object.entries(combinedFactions)) {
        // Get base factions (exclude the combined faction name and Sentients)
        const baseFactions = componentFactions.filter(f => 
          f !== combinedFaction && f !== 'The Sentients'
        );
        
        // Get strategy's base factions (exclude Sentients)
        const strategyBaseFactions = allStrategyFactions.filter(f => f !== 'The Sentients');
        
        // Check if strategy has all the base factions of this combined faction
        const strategyBaseSet = new Set(strategyBaseFactions);
        const baseSet = new Set(baseFactions);
        
        // Match if strategy has all base factions
        const hasAllBaseFactions = baseFactions.every(f => strategyBaseSet.has(f));
        
        if (hasAllBaseFactions) {
          // Calculate match score: how many base factions match, and how many extra factions there are
          const matchingFactions = strategyBaseFactions.filter(f => baseSet.has(f)).length;
          const extraFactions = strategyBaseFactions.filter(f => !baseSet.has(f)).length;
          
          // Prefer exact matches (no extra factions), but also accept matches with extra factions
          // Score = matching factions - (extra factions * 0.5)
          const score = matchingFactions - (extraFactions * 0.5);
          
          console.log('🔵 Combined faction candidate:', combinedFaction, 
            'base factions:', baseFactions,
            'strategy has:', strategyBaseFactions,
            'score:', score);
          
          // Prefer exact match first
          if (extraFactions === 0 && matchingFactions === baseFactions.length) {
            matchedFaction = combinedFaction;
            console.log('🔵 Exact match found:', combinedFaction);
            break;
          }
          
          // Track best match if no exact match yet
          if (score > bestMatchScore) {
            bestMatchScore = score;
            bestMatch = combinedFaction;
          }
        }
      }
      
      // If we found a best match but no exact match, use the best match
      if (!matchedFaction && bestMatch) {
        matchedFaction = bestMatch;
        console.log('🔵 Using best match combined faction:', bestMatch, 'with score:', bestMatchScore);
      }
      
      // If no combined faction match, use the most common faction (excluding Sentients)
      if (!matchedFaction) {
        const nonSentientCounts = {};
        for (const [faction, count] of Object.entries(factionCounts)) {
          if (faction !== 'The Sentients') {
            nonSentientCounts[faction] = count;
          }
        }
        
        // Find the most common faction
        let maxCount = 0;
        for (const [faction, count] of Object.entries(nonSentientCounts)) {
          if (count > maxCount) {
            maxCount = count;
            matchedFaction = faction;
          }
        }
        
        // Fallback to first non-Sentient faction
        if (!matchedFaction) {
          matchedFaction = allStrategyFactions.find(f => f !== 'The Sentients') || allStrategyFactions[0];
        }
        
        console.log('🔵 Using most common faction:', matchedFaction);
      }
      
      if (matchedFaction && matchedFaction !== selectedFaction && matchedFaction !== 'The Sentients') {
        console.log('🔵 Switching faction from', selectedFaction, 'to', matchedFaction);
        // Store the strategy info to continue loading after faction switch
        pendingStrategyLoadAfterFactionSwitch.current = { strategyId, selected };
        setSelectedFaction(matchedFaction);
        // The useEffect will reload orders when selectedFaction changes
        // Then the pendingStrategyLoadAfterFactionSwitch useEffect will continue loading
        strategyLoadedByOnChange.current = false;
        return; // Exit early, will continue after orders reload
      }
    }
    
    // If orders aren't loaded yet, the useEffect will handle loading when they arrive
    if (orders.length === 0) {
      console.log('🔵 Orders not loaded yet, useEffect will handle loading');
      strategyLoadedByOnChange.current = false; // Let useEffect handle it
      return;
    }
    
    // Validate strategy ownership
    try {
      const validationRes = await fetch(`${API_URL}/strategy/${strategyId}/validate`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      const validation = await validationRes.json();
      setStrategyValidation(validation);
      
      // Check if strategy has unowned orders
      // Support both orderId (camelCase) and order_id (snake_case)
      const hasUnowned = selected.orders.some(({ orderId, order_id, isOwned, availability }) => {
        if (availability === 'Created') return false;
        return isOwned === false;
      });
      
      if (hasUnowned || !validation.valid) {
        // Load ALL orders including unowned ones, mark them for visual distinction
        const newMessages = [];
        const newCounts = {};
        const missingOrderIds = [];
        
        // First pass: collect orders that are already loaded and identify missing ones
        selected.orders.forEach(({ orderId, order_id, count, isOwned, availability }) => {
          // Support both orderId (camelCase) and order_id (snake_case)
          const id = orderId || order_id;
          if (!id) {
            console.log('🔵 Order missing ID:', { orderId, order_id, count });
            return;
          }
          
          const order = (orders || []).find(o => o.id === id);
          if (order) {
            // Add ALL orders (owned and unowned) but mark unowned ones
            const owned = isOwned !== false || availability === 'Created';
            for (let i = 0; i < count; i++) {
              newMessages.push(createSidebarOrderEntry(order, owned));
            }
            newCounts[id] = count;
          } else {
            // Fetch ALL missing orders (owned and unowned)
            missingOrderIds.push({ id, count, isOwned, availability });
          }
        });
        
        // Fetch missing orders
        if (missingOrderIds.length > 0) {
          console.log('🔵 Fetching', missingOrderIds.length, 'missing orders (with unowned)');
          const fetchPromises = missingOrderIds.map(({ id }) =>
            fetch(`${API_URL}/order/${id}?${buildOrderDetailLangQuery(i18nInstance.language)}`, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            }).then(res => res.ok ? res.json() : null).catch(err => {
              console.error(`Failed to fetch order ${id}:`, err);
              return null;
            })
          );
          
          const fetchedOrders = await Promise.all(fetchPromises);
          
          // Store missing orders for grid display (only those not in current orders array)
          const validFetchedOrders = fetchedOrders.filter(o => {
            if (!o) return false;
            // Only include if not already in orders array
            return !(orders || []).find(existingOrder => existingOrder.id === o.id);
          });
          
          // Also include orders from validation that user doesn't own enough of (even if in current orders array)
          const insufficientOrderIds = new Set((validation.unownedOrders || validation.insufficientOrders || []).map(o => o.id));
          const insufficientOrdersInGrid = (orders || []).filter(o => insufficientOrderIds.has(o.id));
          
          // Combine both types of missing orders
          const allMissingOrders = [...validFetchedOrders, ...insufficientOrdersInGrid];
          // Remove duplicates
          const uniqueMissingOrders = Array.from(new Map(allMissingOrders.map(o => [o.id, o])).values());
          setMissingOrders(uniqueMissingOrders);
          
          // Add fetched orders to sidebar (both owned and unowned)
          fetchedOrders.forEach((order, index) => {
            if (order) {
              const { count, isOwned, availability } = missingOrderIds[index];
              const owned = isOwned !== false || availability === 'Created';
              for (let i = 0; i < count; i++) {
                newMessages.push(createSidebarOrderEntry(order, owned));
              }
              newCounts[order.id] = count;
            }
          });
        } else {
          // Check if there are insufficient orders from validation (even if all orders are in current array)
          const insufficientOrderIds = new Set((validation.unownedOrders || validation.insufficientOrders || []).map(o => o.id));
          const insufficientOrdersInGrid = (orders || []).filter(o => insufficientOrderIds.has(o.id));
          setMissingOrders(insufficientOrdersInGrid);
        }
        
        // Sort by cost
        newMessages.sort((a, b) => a.cost - b.cost);
        console.log('🔵 Setting sidebar messages (with unowned):', newMessages.length);
        setSidebarMessages(newMessages);
        setOrderCounts(newCounts);
        // Store original orders for change detection
        commitStrategyLoadBaseline(selected.orders);
        strategyLoadedByOnChange.current = false; // Reset flag after loading
      } else {
        // All orders are owned, load normally
        const newMessages = [];
        const newCounts = {};
        const missingOrderIds = [];
        
        // First pass: collect orders that are already loaded and identify missing ones
        selected.orders.forEach(({ orderId, order_id, count }) => {
          // Support both orderId (camelCase) and order_id (snake_case)
          const id = orderId || order_id;
          if (!id) {
            console.log('🔵 Order missing ID:', { orderId, order_id, count });
            return;
          }
          
          const order = (orders || []).find(o => o.id === id);
          if (order) {
            for (let i = 0; i < count; i++) {
              newMessages.push(createSidebarOrderEntry(order, true));
            }
            newCounts[id] = count;
          } else {
            missingOrderIds.push({ id, count });
          }
        });
        
        // Fetch missing orders
        if (missingOrderIds.length > 0) {
          console.log('🔵 Fetching', missingOrderIds.length, 'missing orders');
          const fetchPromises = missingOrderIds.map(({ id }) =>
            fetch(`${API_URL}/order/${id}?${buildOrderDetailLangQuery(i18nInstance.language)}`, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            }).then(res => res.ok ? res.json() : null).catch(err => {
              console.error(`Failed to fetch order ${id}:`, err);
              return null;
            })
          );
          
          const fetchedOrders = await Promise.all(fetchPromises);
          
          // Store missing orders for grid display (only those not in current orders array)
          const validFetchedOrders = fetchedOrders.filter(o => {
            if (!o) return false;
            // Only include if not already in orders array
            return !(orders || []).find(existingOrder => existingOrder.id === o.id);
          });
          setMissingOrders(validFetchedOrders);
          
          // Add fetched orders to sidebar
          fetchedOrders.forEach((order, index) => {
            if (order) {
              const { count } = missingOrderIds[index];
              for (let i = 0; i < count; i++) {
                newMessages.push(createSidebarOrderEntry(order, true));
              }
              newCounts[order.id] = count;
            }
          });
        } else {
          // No missing orders, clear the state
          setMissingOrders([]);
        }
        
        // Sort by cost
        newMessages.sort((a, b) => a.cost - b.cost);
        console.log('🔵 Setting sidebar messages (all owned):', newMessages.length);
        setSidebarMessages(newMessages);
        setOrderCounts(newCounts);
        // Store original orders for change detection
        commitStrategyLoadBaseline(selected.orders);
        strategyLoadedByOnChange.current = false; // Reset flag after loading
      }
    } catch (err) {
      console.error('Failed to validate strategy:', err);
      // Fallback to loading without validation
      const newMessages = [];
      const newCounts = {};
      const missingOrderIds = [];
      
      // First pass: collect orders that are already loaded and identify missing ones
      selected.orders.forEach(({ orderId, order_id, count }) => {
        // Support both orderId (camelCase) and order_id (snake_case)
        const id = orderId || order_id;
        if (!id) {
          console.log('🔵 Order missing ID (fallback):', { orderId, order_id, count });
          return;
        }
        
        const order = (orders || []).find(o => o.id === id);
        if (order) {
          for (let i = 0; i < count; i++) {
            newMessages.push(createSidebarOrderEntry(order, true));
          }
          newCounts[id] = count;
        } else {
          missingOrderIds.push({ id, count });
        }
      });
      
      // Fetch missing orders
      if (missingOrderIds.length > 0) {
        console.log('🔵 Fetching', missingOrderIds.length, 'missing orders (fallback)');
        const fetchPromises = missingOrderIds.map(({ id }) =>
          fetch(`${API_URL}/order/${id}?${buildOrderDetailLangQuery(i18nInstance.language)}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }).then(res => res.ok ? res.json() : null).catch(err => {
            console.error(`Failed to fetch order ${id}:`, err);
            return null;
          })
        );
        
          const fetchedOrders = await Promise.all(fetchPromises);
          
          // Store missing orders for grid display
          const validFetchedOrders = fetchedOrders.filter(o => o !== null);
          setMissingOrders(validFetchedOrders);
          
          // Add fetched orders to sidebar
          fetchedOrders.forEach((order, index) => {
            if (order) {
              const { count } = missingOrderIds[index];
              for (let i = 0; i < count; i++) {
                newMessages.push(createSidebarOrderEntry(order, true));
              }
              newCounts[order.id] = count;
            }
          });
        } else {
          // No missing orders, clear the state
          setMissingOrders([]);
        }
        
        // Sort by cost
        newMessages.sort((a, b) => a.cost - b.cost);
        console.log('🔵 Setting sidebar messages (fallback):', newMessages.length);
        setSidebarMessages(newMessages);
        setOrderCounts(newCounts);
      // Store original orders for change detection
      commitStrategyLoadBaseline(selected.orders);
      strategyLoadedByOnChange.current = false; // Reset flag after loading
    }
  }}
/>
{showStrategyPlayer1DevControls && (
  <div
    className="strategy-dev-load-by-key"
    style={{
      marginTop: '10px',
      padding: '10px',
      border: '1px dashed rgba(255, 200, 80, 0.6)',
      borderRadius: '6px',
      backgroundColor: 'rgba(0, 0, 0, 0.35)',
    }}
  >
    <div style={{ fontSize: '0.85em', color: '#ffc850', marginBottom: '6px', fontWeight: 600 }}>
      Load by strategy key (dev)
    </div>
    <input
      type="text"
      value={devStrategyKeyInput}
      onChange={(e) => setDevStrategyKeyInput(e.target.value)}
      placeholder="64-char strategy_key (hex)"
      spellCheck={false}
      style={{
        width: '100%',
        boxSizing: 'border-box',
        padding: '8px',
        fontSize: '0.75em',
        fontFamily: 'monospace',
        marginBottom: '8px',
        borderRadius: '4px',
        border: '1px solid #555',
        backgroundColor: '#1a1a1a',
        color: '#eee',
      }}
    />
    <button
      type="button"
      disabled={devStrategyKeyLoading || !devStrategyKeyInput.trim()}
      onClick={() => {
        playSoundEffect('/sounds/click.mp3');
        handleDevLoadStrategyByKey();
      }}
      style={{
        width: '100%',
        padding: '8px 12px',
        fontSize: '0.9em',
        border: 'none',
        borderRadius: '6px',
        backgroundColor: devStrategyKeyLoading ? '#666' : '#b8860b',
        color: '#111',
        fontWeight: 'bold',
        cursor: devStrategyKeyLoading || !devStrategyKeyInput.trim() ? 'not-allowed' : 'pointer',
      }}
    >
      {devStrategyKeyLoading ? 'Loading…' : 'Load strategy'}
    </button>
  </div>
)}

<div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
  <button
    onClick={handleOpenSaveDialog}
    style={{
      padding: "12px 24px",
      fontSize: "1.2em",
      border: "none",
      borderRadius: "8px",
      backgroundColor: "rgba(0,0,0,0.6)",
      color: "white",
      cursor: "pointer",
      transition: "background-color 0.3s ease",
      flex: 1
    }}
  >
    {t('strategy.saveStrategy')}
  </button>

  <button
    style={{
      padding: "12px 24px",
      fontSize: "1.2em",
      border: "none",
      borderRadius: "8px",
      backgroundColor: "rgba(0,0,0,0.6)",
      color: "white",
      cursor: selectedStrategyId ? "pointer" : "not-allowed",
      transition: "background-color 0.3s ease",
      flex: 1,
      opacity: selectedStrategyId ? 1 : 0.5
    }}
    disabled={!selectedStrategyId}
    onClick={() => {
      playSoundEffect('/sounds/click.mp3');
      showConfirmation(t('strategy.alerts.deleteConfirm'), (confirmed) => {
        if (!confirmed) return;

        fetch(`${API_URL}/strategy/${selectedStrategyId}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        })
          .then(res => res.json())
          .then(data => {
            showNotification(data.message || t('strategy.alerts.deleted'), 'success');
            setSelectedStrategyId('');
            setOriginalStrategyOrders(null);
            // Refresh strategy list
            setUserStrategies(prev => prev.filter(s => s.id !== parseInt(selectedStrategyId)));
            setSidebarMessages([]);
            setOrderCounts({});
          })
          .catch(err => {
            console.error("Delete failed:", err);
            showNotification(t('strategy.alerts.deleteFailed'), 'error');
          });
      });
    }}
  >
    {t('strategy.deleteStrategy')}
  </button>
</div>

<div style={{ marginTop: "10px", fontWeight: "bold" }}>
  {t('strategy.totalOrders')} {(sidebarMessages || []).length}
</div>

</div>

</div>

 <OrderSidebarOrderList
  items={sidebarMessages}
  getGroupKey={sidebarMessageGroupKey}
  resolveOrderRecord={(msg) => resolveSidebarOrderRecord(msg, orders, missingOrders)}
  getDisplayName={(msg) => msg.name}
  getDisplayCost={(msg) => msg.cost}
  costLabel={t('strategy.cost')}
  isItemUnowned={(msg) => msg.isOwned === false}
  onItemMouseEnter={(msg, group, e) => {
    const order = resolveSidebarOrderRecord(msg, orders, missingOrders);
    if (order) {
      const rect = e.currentTarget.getBoundingClientRect();
      setSidebarTooltipRect({
        index: group.firstIndex,
        top: rect.top,
        bottom: rect.bottom,
        left: e.clientX,
      });
    }
  }}
  onItemMouseLeave={() => setSidebarTooltipRect(null)}
  onItemClick={(msg) => {
    playSoundEffect('/sounds/remove_order.mp3');
    setSidebarMessages((prev) => {
      const removeAt = prev.findIndex((m) => sidebarMessagesMatchForRemoval(m, msg));
      if (removeAt === -1) return prev;
      return prev.filter((_, i) => i !== removeAt);
    });
    const order = resolveSidebarOrderRecord(msg, orders, missingOrders);
    const orderIdForCount =
      msg.orderId ??
      order?.id ??
      (missingOrders || []).find((o) => o.name === msg.name && o.cost === msg.cost)?.id;
    if (orderIdForCount != null) {
      setOrderCounts((prev) => decrementOrderCountMap(prev, orderIdForCount));
    }
    markStrategySidebarDirty();
  }}
  getItemTitle={(msg) =>
    msg.isOwned === false
      ? 'You do not own this order - Click to remove'
      : t('strategy.clickToRemove')
  }
/>

{/* Tooltip for sidebar orders - rendered via portal to avoid clipping. Uses stored rect only (no querySelector in render). */}
{sidebarTooltipRect !== null && createPortal((() => {
  const { index: hoveredSidebarIndex, top: orderItemTop, bottom: orderItemBottom, left: tooltipLeft } = sidebarTooltipRect;
  const msg = (sidebarMessages || [])[hoveredSidebarIndex];
  const order = msg ? resolveSidebarOrderRecord(msg, orders, missingOrders) : null;
  const isUnowned = msg?.isOwned === false;
  
  if (!order) return null;
  
  const tooltipTop = orderItemBottom + 5;
  const tooltipWidth = 250 * 0.8;
  const tooltipHeight = 380 * 0.8;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  let adjustedLeft = tooltipLeft;
  let adjustedTop = tooltipTop;
  let transformOrigin = 'top left';
  
  if (adjustedLeft + tooltipWidth > viewportWidth) {
    adjustedLeft = viewportWidth - tooltipWidth - 10;
  }
  if (adjustedLeft < 10) {
    adjustedLeft = 10;
  }
  if (adjustedTop + tooltipHeight > viewportHeight - 10) {
    const gap = 20;
    const cellsUp = 3;
    const pixelsPerCell = 20;
    const additionalOffset = cellsUp * pixelsPerCell;
    adjustedTop = orderItemTop - tooltipHeight - gap - additionalOffset;
    transformOrigin = 'bottom left';
    if (adjustedTop < 10) {
      adjustedTop = 10;
      transformOrigin = 'top left';
    }
  }
  if (adjustedTop < 10) {
    adjustedTop = 10;
  }
  
  return (
    <div
      style={{
        position: 'fixed',
        left: `${adjustedLeft}px`,
        top: `${adjustedTop}px`,
        zIndex: 99999,
        pointerEvents: 'none', // Don't interfere with mouse events on order items
        transform: 'scale(0.8)',
        transformOrigin: transformOrigin,
        opacity: 1,
        isolation: 'isolate',
        filter: isUnowned ? 'grayscale(100%)' : 'none'
      }}
    >
      <div style={{ opacity: 1, zIndex: 99999 }}>
        <OrderDesign
          key={`sidebar-${order.id}-${getOrderArtVariant(order.id)}`}
          order={withAlternateArtDisplayOrder(order)}
          count={0}
          minimal={false}
          noMargin={true}
          lazyLoad={false}
          isOOR={false}
        />
      </div>
    </div>
  );
})(), document.body)}

{/* Created (dev): reverse tooltip — parent deploy orders that spawn/grant the hovered order. */}
{selectedFaction === DEV_CREATED_CATALOG_FACTION &&
  createdDevParentPreviewCards &&
  deploySpawnAnchorRect &&
  createPortal(
    <div
      className="strategy-deploy-spawn-portal strategy-created-dev-source-portal"
      style={{
        position: 'fixed',
        left: `${(() => {
          const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
          const n = createdDevParentPreviewCards.length;
          const reserve = Math.min(56 + n * 212, vw - 12);
          return Math.min(deploySpawnAnchorRect.right + 8, vw - reserve);
        })()}px`,
        top: `${Math.max(
          8,
          Math.min(
            deploySpawnAnchorRect.top,
            (typeof window !== 'undefined' ? window.innerHeight : 900) - 315
          )
        )}px`,
        zIndex: 100000,
        pointerEvents: 'none',
        transform: `scale(${viewportScale * STRATEGY_DEPLOY_SPAWN_PREVIEW_SCALE})`,
        transformOrigin: 'top left',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: '10px',
      }}
    >
      {createdDevParentPreviewCards.map((order) => (
        <OrderDesign
          key={order.id}
          order={withAlternateArtDisplayOrder(order)}
          count={0}
          minimal
          lazyLoad={false}
          isOOR={false}
        />
      ))}
    </div>,
    document.body
  )}

{/* Normal factions: spawn/grant preview (child unit). */}
{deploySpawnPreviewCard && deploySpawnAnchorRect && createPortal(
  <div
    className="strategy-deploy-spawn-portal"
    style={{
      position: 'fixed',
      left: `${Math.min(
        deploySpawnAnchorRect.right + 8,
        (typeof window !== 'undefined' ? window.innerWidth : 1200) - 340
      )}px`,
      top: `${Math.max(
        8,
        Math.min(
          deploySpawnAnchorRect.top,
          (typeof window !== 'undefined' ? window.innerHeight : 900) - 315
        )
      )}px`,
      zIndex: 100000,
      pointerEvents: 'none',
      transform: `scale(${viewportScale * STRATEGY_DEPLOY_SPAWN_PREVIEW_SCALE})`,
      transformOrigin: 'top left',
    }}
  >
    <OrderDesign
      order={deploySpawnPreviewCard}
      count={0}
      minimal
      lazyLoad={false}
      isOOR={false}
    />
  </div>,
  document.body
)}

<TutorialReplayButton
  user={user}
  onReplay={() => {
    setTutorialStrategyStep(1);
    setShowTutorialStrategy(true);
  }}
/>

{showTutorialStrategy && createPortal(
  <TutorialMessageBox
    image="/images/tutorial_wait.png"
    text={tutorialStrategyStep === 1 ? t('strategy.tutorialText') : t('strategy.tutorialText2')}
    voiceKey={tutorialStrategyStep === 1 ? 'strategy.tutorialText' : 'strategy.tutorialText2'}
    continueButtonImagePath={BATTLE_UI_END_TURN_BUTTON_PATH}
    continueButtonClassName="strategy-modal-close-btn"
    paintedButtonSurfaceFilter={MENU_PAINTED_SURFACE_FILTER}
    continueLabelKey={tutorialStrategyStep === 1 ? 'welcome.tutorial.continue' : 'strategy.tutorialClose'}
    onContinue={async () => {
      if (tutorialStrategyStep === 1) {
        setTutorialStrategyStep(2);
        return;
      }
      if (!isReplayingStrategyTutorial) {
        const token = localStorage.getItem('token');
        if (token) {
          await fetch(`${API_URL}/tutorial/mark-seen`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ messageKey: 'strategy' }),
          });
          markSeenLocally('strategy');
        }
      }
      setShowTutorialStrategy(false);
    }}
  />,
  document.body
)}

{showSaveDialog && createPortal(
  <div className={`modal-overlay${isMobile ? ' mobile' : ''}`} style={{ zIndex: 1000000 }} onClick={(e) => {
    // Only close if clicking the overlay itself, not the content
    if (e.target.className.includes('modal-overlay')) {
      setShowSaveDialog(false);
    }
  }}>
    <div className={`modal-content${isMobile ? ' mobile' : ''}`} style={{ zIndex: 1000001, position: 'relative', pointerEvents: 'auto', cursor: 'auto' }}>
      <h3>{t('strategy.modal.saveTitle')}</h3>
      <label>
        {t('strategy.modal.name')}<br />
        <input
          type="text"
          value={modalStrategyName}
          onChange={e => setModalStrategyName(e.target.value)}
          style={{ width: "100%" }}
        />
      </label>
      <br />
      <label>
        {t('strategy.modal.description')}<br />
        <textarea
          value={modalDescription}
          onChange={e => setModalDescription(e.target.value)}
          rows={3}
          style={{ width: "100%", resize: "vertical" }}
        />
      </label>
      <br />
      <label>
        <input
          type="checkbox"
          checked={modalIsPrivate}
          onChange={e => setModalIsPrivate(e.target.checked)}
        />
        {t('strategy.modal.private')}
      </label>
      <br />
      <div style={{ marginTop: 10, display: "flex", gap: 10 }}>
        <button onClick={() => {
          playSoundEffect('/sounds/click.mp3');
          handleSaveStrategy();
        }}>{t('strategy.modal.save')}</button>
        <button onClick={() => {
          playSoundEffect('/sounds/click.mp3');
          setShowSaveDialog(false);
        }}>{t('strategy.modal.cancel')}</button>
      </div>
    </div>
  </div>,
  document.body
)}

{showStrategyStatisticsModal && typeof document !== 'undefined' && createPortal(
  <div
    className="strategy-statistics-modal-overlay"
    role="dialog"
    aria-modal="true"
    aria-labelledby="strategy-statistics-modal-title"
    style={{ zIndex: 1000000, pointerEvents: 'auto' }}
    onClick={(e) => {
      if (e.target === e.currentTarget) {
        playSoundEffect('/sounds/click.mp3');
        setShowStrategyStatisticsModal(false);
      }
    }}
  >
    <div
      className="strategy-statistics-modal"
      style={{ pointerEvents: 'auto' }}
      onClick={(e) => e.stopPropagation()}
    >
      <h3 id="strategy-statistics-modal-title">
        {t('strategy.statisticsTitle', { defaultValue: 'Strategy statistics' })}
      </h3>
      <button
        type="button"
        className="strategy-statistics-modal-close"
        onClick={() => {
          playSoundEffect('/sounds/click.mp3');
          setShowStrategyStatisticsModal(false);
        }}
      >
        {t('strategy.statisticsClose', { defaultValue: 'Close' })}
      </button>
      <AttackStrategyStatisticsPanel orders={strategyStatisticsOrders} layout="row" />
    </div>
  </div>,
  document.body
)}

{statTooltipPortal}

{/* Custom Notification and Confirmation Components */}
{NotificationComponent}
{ConfirmationComponent}

{showStrategyPlayer1DevControls ? (
  <HideDevElementsForScreenshotButton
    user={user}
    onHide={hideDevElementsForScreenshotNow}
  />
) : null}

</div>
</div>
</div>
</div>
  );
}

export default Strategy;